/**
 * ⛔⭐ **RASTRO `[28/8/2026]` — ESTE ARCHIVO DEJÓ DE SER LA PANTALLA ENTERA.**
 * `AW18`, `AW19` y `AW20` lo convirtieron en la **raíz de composición**: el
 * selector de participante, el hilo por sesiones y el redactor viven ahora en
 * archivos propios del mismo directorio, y `sesiones.ts` —puro— es lo único de
 * todo el grupo que se rinde sin navegador.
 * ⭐ **El archivo NO se movió ni se renombró, y es una restricción medida**:
 * `gates/pantalla-simulador.spec.ts` afirma que `chat.tsx` EXISTE (`SITIOS`), así
 * que mudarlo habría puesto `AW16` en rojo. Lo de abajo describe correctamente por
 * qué la pantalla es como es, y **no se borra**.
 *
 * ⭐⭐ `AW16` · **El simulador de chat.** La mitad que un humano toca del
 * simulador que `ADR-0196` compuso: **motor REAL, canal FALSO, base LOCAL**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ①  LO QUE HACE DE ESTO UN INSTRUMENTO Y NO UNA DEMO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Una sola decisión: el desenlace CRUDO al lado de cada mensaje.** Cada entrante
 * que el operador manda muestra, junto a su burbuja, **el literal de
 * `InboundOutcome` que el motor devolvió** —`encolado`, `modalidad`,
 * `emisor-desconocido`, `duplicado`, `canal-no-ruteable`, `interactivo-sin-ruteo`,
 * `interactivo-demo`, `interactivo-salida`—. **Sin traducir, sin agrupar, y sin
 * esconder los que «no hacen nada».**
 *
 * ⚠️ **De dónde cuelga este requisito, dicho con precisión porque es fácil
 * atribuirlo mal: es una DECISIÓN DEL DUEÑO, NO DE UN ADR.** `ADR-0196` dice
 * explícitamente lo contrario de lo que se le suele adjudicar: *«No decide la
 * superficie del simulador: ni ruta, ni pantalla, ni si vive en `bot-web`, en el
 * Management Tool o en un script.»* La fuente es **`D9` del plan del 26/8**
 * (`docs/_reports/jornada/plan-2026-08-26.md:32`, *«El simulador muestra el
 * desenlace crudo de cada entrante»*), tomada en conversación con el dueño.
 *
 * ⛔ **Las tres salidas cómodas, y las tres las pide un revisor de UI con razón
 * desde su punto de vista:**
 *
 *   ① **traducir el literal a lenguaje de producto** —`interactivo-sin-ruteo` →
 *      «el botón no está conectado todavía»—. Se lee mejor **y rompe exactamente
 *      para lo que la pantalla existe**: el valor del simulador es que un operador
 *      vea **el mismo literal que un examen cuenta**, para poder llevarlo a un
 *      ticket, a un `grep` o a una conversación con quien escribió el módulo. Una
 *      traducción crea **un segundo vocabulario** para los mismos ocho valores, y
 *      el día que entre un noveno la traducción no lo tiene y la pantalla muestra
 *      un hueco;
 *   ② **derivarlo** —«enviado» si hubo respuesta, «ignorado» si no—. Es una
 *      **segunda opinión sobre algo que el motor ya dijo**, y discrepa en los
 *      casos que más importan: `duplicado` y `modalidad` producen una respuesta y
 *      `encolado` no produce ninguna **al instante**, así que la derivación los
 *      clasifica exactamente al revés;
 *   ③ la más silenciosa: **esconder los desenlaces que «no hacen nada»** para que
 *      el chat se vea limpio. `emisor-desconocido`, `duplicado` y
 *      `canal-no-ruteable` son justamente los que un operador va a estar buscando
 *      cuando el simulador «no anda».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ②  LO QUE ESTA PANTALLA **NO** VA A PODER HACER — `CH8` ESTÁ DIFERIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `specs/F7-integracion-whatsapp/etapas/canal.md:156` lleva un `defer:` formal y
 * vivo hasta el **2026-10-31** (`decidido_por: { quien: "Elige con la PM" }`). ⇒
 * **«Empecemos» y «Pásame con una persona» NO tienen ruteo**: caen en
 * `interactivo-sin-ruteo` (`modules/channel/src/entrada/recibir.ts:1079`), o en
 * `interactivo-demo` (`:1076`) si el modo demo está encendido en el entorno de
 * `bot-web`.
 *
 * ⭐ **Y eso es el argumento A FAVOR de esta pantalla, no en contra.** Hoy esa
 * ausencia vive en un `reason:` de miles de caracteres adentro de un `spec.md`;
 * **la prosa no la mira ningún detector y ningún operador la lee.** Con el
 * simulador se manda el botón y **se ve el literal**: el sistema lo registró **y
 * no lo ruteó**, dicho por el motor real contra la base real.
 *
 * ⛔ **Y el `defer:` ya escribió qué se pierde, y la pantalla no lo suaviza:** *«en
 * esta ronda un botón se representa, se persiste y no produce la conducta que cada
 * uno merece. Para «Empecemos» eso es una métrica que falta; para «Pásame con una
 * persona» es un participante que pidió un humano y no lo recibe.»*
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ③  LA REGLA QUE GOBIERNA TODA LA PANTALLA — `ADR-0040 §②`, TEXTUAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *«**se puede fingir lo que no promete nada que el sistema no vaya a cumplir. NO
 * SE PUEDE FINGIR UNA OBLIGACIÓN.**»*
 *
 * ⇒ **esta pantalla no dibuja NINGUNA confirmación que la base no haya escrito.**
 * Nada de «listo, te di de baja», ni un tilde de «enviado», ni un estado dibujado
 * por el cliente. Todo lo que se ve en el hilo llegó por el cable: **el desenlace
 * salió del cuerpo del `200` de `POST /entrada`, y las respuestas salieron de
 * `GET /entregas`**, que lee lo que quedó persistido.
 *
 * `ADR-0040 §②` midió el daño exacto de lo contrario: *«la persona cree que se dio
 * de baja, el sistema sigue considerándola elegible, y le sigue enviando»*. **Acá
 * el que se lo cree no es el participante sino el operador, que es peor en un
 * sentido concreto: el operador es quien reporta si el sistema funciona**, y un
 * simulador que le confirma lo que no pasó produce un informe verde sobre un hueco.
 *
 * ⛔⛔ **Y LA VARIANTE QUE PARECE LEGÍTIMA PORQUE USA UN MECANISMO QUE EXISTE:
 * encender `BOT_WEB_DEMO_MODE`** para que los dos botones de `CH8` contesten algo.
 * `ADR-0040 §⑤` la cierra textual —*«Esto NO cierra `CH8`, y el `defer:` no se
 * toca […] el modo demo no es una implementación parcial de `CH8`: es un andamio
 * de demostración, y las dos cosas tienen que poder distinguirse»*— y el daño es
 * específico: **taparlo haría creer que el ruteo existe**, que es lo contrario de
 * lo que esta pantalla vino a mostrar.
 *
 * ⚠️ **La pantalla no lo prohíbe ni lo apaga** —el flag es del entorno de
 * `bot-web` y no de acá—: si el entorno lo tiene encendido, la pantalla **muestra
 * `interactivo-demo` crudo**, que es exactamente la distinción que `§⑤` pide. Lo
 * que esta etapa no hace es **encenderlo, pedirlo ni asumirlo**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ④  NI UN TELÉFONO, NI UN NOMBRE, NI UN DATO DE UNA PERSONA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ni real, ni inventado con forma real: en ningún `placeholder`, valor por
 * defecto, comentario o ejemplo. **Los formatos se DESCRIBEN en palabras.**
 * `contracts/channel/fake.ts` escribió la regla y su razón —*«Son datos de
 * menores: un número con esa forma es el de alguien, y queda en git para
 * siempre»*— y `ADR-0196` §«Lo que NO decide» la hereda entera: *«No autoriza a
 * inventar teléfonos ni nombres.»* ⚠️ **Y por esta pantalla pasan mensajes de
 * menores**, no sólo por el proceso.
 *
 * ⇒ **`destinatario` NO SE DIBUJA.** Viene en cada entrega y es el teléfono del
 * participante en claro (`apps/bot-web/src/entregas.ts`, `destinatario:
 * fila.telefono`). No aporta nada —el operador acaba de escribir con quién está
 * conversando— y dibujarlo pondría un teléfono en la pantalla y en cada captura
 * que alguien pegue en un ticket.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ ⑤  LA PANTALLA NO DA DE ALTA PARTICIPANTES Y NO INVENTA NINGUNO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El alta ya tiene su superficie (`AW7`, `src/alta.tsx`) y el padrón la suya
 * (`AW15`, `src/padron/`). Un simulador que crease su propio participante «de
 * prueba» **escribiría una fila real en el padrón real del brazo**, indistinguible
 * de un participante de verdad desde cualquier consulta — que es la misma
 * contaminación que `ADR-0196` evita con la base local, producida por el otro
 * camino.
 *
 * ⇒ **el participante se ESCRIBE acá y sale del padrón.**
 * `modules/channel/src/entrada/recibir.ts:810-813` corta con `"emisor-desconocido"`
 * **antes de persistir y antes de encolar** si no está en el padrón del brazo
 * —*«Un desconocido sale por acá SIN un `POST` y SIN un turno registrado. No hay
 * nada después de este `return`»*—. **Sin un participante enrolado el simulador
 * sólo puede producir `emisor-desconocido`, y eso no es un defecto de la pantalla:
 * es el motor real diciendo la verdad.**
 *
 * ⛔⭐ RASTRO `[28/9/2026]` — `R3-UI6` (A10/R3-E4). Lo de arriba hablaba de «un
 * participante enrolado» y de caer «en el padrón del brazo»: era el modelo ① (una
 * lista de la configuración). `ADR-0232` midió que el motor **no consulta el
 * padrón**: `recibir.ts` resuelve al emisor contra `directorio`, el `Map` que
 * `composicion.ts` arma desde `F7_PARTICIPANTS`. El selector de ① se retiró (ver el
 * rastro de `selector-participante.tsx`) y el participante se elige del padrón con
 * `GET padron` (`ADR-0228`). Lo que sigue siendo cierto: una dirección del padrón
 * que no esté entre los participantes que la instancia declaró produce
 * `emisor-desconocido` — el motor diciendo la verdad.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ ⑥  LA PANTALLA PREGUNTA; NADIE LE EMPUJA — Y `GET /sse` SE DESCARTÓ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El refresco es la pantalla pidiendo `GET /entregas` con el `cursor` que la
 * respuesta anterior devolvió. **`GET /sse` existe y sería la opción natural para
 * un chat**, y se descarta con dos razones medidas:
 *
 *   ① es **una superficie más** que el navegador tendría que alcanzar, y
 *      `ADR-0197` §«Lo que NO decide» pide lo contrario —*«no hay ninguna razón
 *      para que las cinco lo estén. Acotarlas es la defensa más barata»*—;
 *   ② **`apps/management-ui` no tiene una sola conexión persistente hoy** (medido
 *      el 26/8/2026, ANTES de esta etapa: `grep -rn 'EventSource' src/` ⇒ cero;
 *      después de esta etapa el único hit es **esta línea**, que es la cita de la
 *      medición y no una conexión), y `AW1` ya fijó la
 *      disciplina: una conexión persistente crea *«un segundo camino hacia el
 *      mismo estado: dos lugares que pueden discrepar»*.
 *
 * ⚠️ **Y lo que se pierde va dicho: este chat se va a sentir menos vivo que uno
 * con push.** Decisión abierta, dueño `web-dev` con el TL.
 *
 * ⚠️ **EL INTERVALO NO LO FIJA NINGUNA FUENTE Y NO SE ELIGE ACÁ.** Se usa
 * `config.refrescoMs`, cuyo default de instancia es `5_000` y es **el de una
 * tabla, no el de un chat**. ⛔ **No se resuelve poniendo un número chico a mano
 * acá**: eso saca el valor del único lugar donde el operador de una instancia lo
 * puede cambiar. Hueco reportado, igual que `AW4` y `AW15` reportaron el
 * `pageSize`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ ⑦  UN PASE CONTRA ESTA PANTALLA NO ES UN PASE REAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0196` §Consecuencias lo hereda de `ADR-0135`: *«un pase medido contra un
 * transporte SIMULADO y uno medido contra el sistema REAL no se distinguen hoy en
 * el registro»*, que impuso `sujeto: real|simulado` obligatorio en todo `estado:
 * aprobado`. ⇒ **ningún caso de QA ejercido contra esta pantalla se publica como
 * `sujeto: real`.** Es una obligación heredada, no una que invente esta etapa, y
 * va dicha **en la pantalla** y no sólo acá.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { JSX } from "react"
import { useConfiguracion } from "../contexto-configuracion.tsx"
import type { BordeSimulador } from "./borde-simulador.ts"
import { CURSOR_INICIAL, LIMITE_DE_TURNOS } from "./borde-simulador.ts"
import type { TurnoEnLaRed } from "./borde-simulador.ts"
import type { BordePadron } from "../padron/borde-padron.ts"
import { SelectorParticipante, type Elegido } from "./selector-participante.tsx"
import { Redactor } from "./redactor.tsx"
import { Hilo } from "./hilo.tsx"
import { ColumnaDePlantilla } from "./plantilla.tsx"
import { SIN_CATALOGO_DE_PLANTILLAS } from "../plantillas/borde-plantillas.ts"
import type { CargarPlantillas } from "../plantillas/borde-plantillas.ts"
import type { BordeEnvios } from "../envios/borde-envios.ts"
import type { BordeConversaciones } from "../conversaciones/borde-conversaciones.ts"
import {
  TAMANO_DE_PAGINA_FUERA_DE_ESCALA,
  tamanoDePaginaDelContrato,
} from "../conversaciones/borde-conversaciones.ts"
import { DESENLACE_ENCOLADO } from "./hilo.tsx"
import type { LineaDeSesion } from "./hilo.tsx"

/**
 * Una línea del hilo.
 *
 * ⭐ **Las dos clases no comparten forma a propósito.** Un entrante es *lo que el
 * operador mandó* + **el desenlace que el motor devolvió**; una saliente es *una
 * fila que quedó escrita en la base*. Unificarlas en «mensaje con texto y estado»
 * obligaría a inventar un estado para las salientes —que no lo tienen— y a
 * inventar un texto para los entrantes que no son texto.
 */
type EnElHilo =
  | {
      readonly clase: "entrante"
      readonly id: number
      /**
       * Lo que se mandó.
       *
       * ⭐⭐ RASTRO · `AW36` · 28/8/2026 — ACÁ DECÍA «descrito por la pantalla», Y ESA
       * ERA LA CAUSA DEL DEFECTO. `[medido 28/8/2026]` Para un entrante de texto el
       * `resumen` era el literal **«Texto libre»** —dos copias en `redactor.tsx`— y
       * la burbuja lo dibuja con `.simulador__contenido`, o sea **a tamaño de
       * cuerpo, en el lugar del contenido**. ⇒ un rótulo viajaba por el canal del
       * contenido, y por eso se veía grande. El arreglo no fue achicar la letra: el
       * texto libre manda **su propio texto**, que es contenido de verdad, y «que
       * era texto» lo dice ahora la marca de tipo, que es canal de rótulo.
       *
       * ⚠️ Los tres gestos que **no tienen texto** —botón ruteable, botón a mano y
       * el escape de tipo— siguen mandando una descripción, porque no hay contenido
       * que poner y una burbuja vacía no dice nada.
       *
       * ⛔ Sigue sin ser NUNCA un dato de persona: lo que entra acá es lo que el
       * operador tipeó o el rótulo de un botón de configuración, jamás el `de`.
       */
      readonly resumen: string
      /**
       * ⭐⭐ `AW36` · **El tipo que se mandó, tal cual salió por el cable.** Antes se
       * recibía en `mandar`, se pasaba al borde y **se descartaba**: la línea que
       * quedaba en el hilo no lo llevaba, así que la pantalla no podía marcarlo.
       *
       * ⛔ No se normaliza ni se traduce. El vocabulario que el motor distingue es
       * `TIPO_TEXTO` / `TIPO_INTERACTIVO` y *«cualquier otro ⇒ `modalidad`»*
       * (`borde-simulador.ts`), y el escape de tipo existe justamente para mandar
       * uno cualquiera: mostrar el literal es lo único que la pantalla puede afirmar.
       */
      readonly tipo: string
      /**
       * ⭐⭐ **El literal que el motor devolvió, tal cual.** `null` sólo cuando el
       * pedido no llegó a producir uno — y entonces `fallo` dice qué pasó.
       */
      readonly desenlace: string | null
      /** El pedido NO llegó. ⛔ Esto **no** es un desenlace y no se dibuja como uno. */
      readonly fallo: string | null
    }
  | {
      readonly clase: "saliente"
      readonly id: number
      /** El contenido tal como quedó escrito. */
      readonly contenido: string
      /** El instante de la base, tal como vino. */
      readonly fecha_hora: string
      /**
       * ⭐ `AW19` · La sesión en la que quedó escrita, **tal como el motor la
       * asignó**. Sólo las SALIENTES la traen: el `POST /entrada` devuelve
       * `{ outcome }` y nada más, así que un entrante no la tiene y **no se le
       * inventa una**. Ver `hilo.tsx` §«el hueco declarado».
       */
      readonly id_sesion: string
      /**
       * ⭐⭐ RASTRO · `ADR-0292` / `B22` · 16/9/2026 — **EL REGISTRO INTERNO DEL TURNO
       * QUE PRODUJO ESTA ENTREGA**, correlacionado por brazo y tiempo (ver el bloque
       * del encabezado). `undefined` = todavía no llegó la fila, y se dibuja así.
       *
       * ⛔ La forma la declara `hilo.tsx` (`LineaVisible`), que es quien la dibuja;
       * acá el campo existe para que `tsc` acompañe el objeto que se le pasa.
       */
      readonly registro?: TurnoEnLaRed
    }

/**
 * Un identificador de mensaje del proveedor, nuevo en cada envío.
 *
 * ⚠️ **Se genera acá porque el proveedor no existe**: `ADR-0196` fija canal FALSO.
 * Repetir uno produce `duplicado` (`recibir.ts:829`), que es un desenlace legítimo
 * y **alcanzable a propósito** desde esta pantalla — hoy sólo si el operador manda
 * dos veces con el mismo valor, cosa que esta versión no ofrece. Ver el informe.
 *
 * ⛔ **No lleva nada derivado del participante.** `providerMessageId` es opaco y
 * viaja a la bitácora de entrantes; componerlo con la dirección metería PII en
 * todos esos lugares a la vez.
 *
 * ⚠️ **`crypto.randomUUID` exige un contexto seguro**, y `localhost` lo es — que
 * es donde esta pantalla corre por construcción (`ADR-0196`: base local;
 * `ADR-0198` §④: `bot-web` escucha en `127.0.0.1`). **No se le pone un respaldo
 * con `Math.random`**: sería un SEGUNDO generador de identificadores para el
 * mismo campo, y el día que el respaldo colisione el síntoma es un `duplicado`
 * que nadie va a atribuir a esto. Si algún día la pantalla se sirviera por HTTP
 * plano desde otra máquina, lo que hay que revisar no es este generador: es que
 * el simulador esté alcanzable desde otra máquina. Reportado.
 */
/**
 * El valor del `<option>` del escape. **No es un nombre de canal**: es un centinela
 * que sólo vive en la pantalla, y por eso lleva una forma que ningún `F7_CHANNELS`
 * puede producir. Si se mandara tal cual, el motor contestaría `canal-no-ruteable`
 * — por eso `canalListo` lo traduce antes de salir.
 */
const CANAL_A_MANO = "\u0000a-mano"

function nuevoIdDeMensaje(): string {
  return crypto.randomUUID()
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ `AW59` · EL INDICADOR DE TURNO EN CURSO — Y SU TECHO, QUE ES LA MITAD
 *              QUE LO VUELVE PERMITIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño pidió, usando el producto: *«cuando está pensando o trabajando en armar
 * una respuesta, debería mostrar un feedback»*. **Dibujarlo es la parte fácil.** La
 * parte que decide si esto se puede entregar es **cuándo se apaga**, y la regla es
 * la del §③ de este mismo archivo, `ADR-0040 §②`, textual:
 *
 * > *«se puede fingir lo que no promete nada que el sistema no vaya a cumplir. NO
 * > SE PUEDE FINGIR UNA OBLIGACIÓN.»*
 *
 *   · **mientras el turno corre de verdad**, el indicador no promete nada que el
 *     sistema no vaya a hacer ⇒ **permitido**;
 *   · **⛔ en cuanto se queda puesto sobre un turno que murió**, afirma que viene
 *     una respuesta y ninguna va a llegar ⇒ **prohibido**.
 *
 * ⇒ **un indicador SIN vencimiento no es una versión incompleta de esto: es la
 * versión prohibida de esto.**
 *
 * ⛔⛔ **Y ESTA PANTALLA NO PUEDE DISTINGUIR LOS DOS CASOS, QUE ES LA RESTRICCIÓN
 * DE LA QUE SALE TODO LO DEMÁS.** `POST /entrada` devuelve `{ outcome }` y nada más
 * —ni el identificador de la fila, ni el instante, ni un estado del turno—, la
 * respuesta viaja **sólo** por el sondeo de `GET /entregas`, y no hay ninguna
 * conexión persistente (§⑥). ⇒ **lo único que se sabe es «mandé algo y todavía no
 * vi respuesta», y eso es indistinguible de «el turno murió»**.
 *
 * ⭐ **LOS DOS RELOJES NO SE CRUZAN, Y ESO ES DELIBERADO.** Hay dos apagados y cada
 * uno compara instantes de UNA sola fuente:
 *
 *   · **el apagado por respuesta** compara **instante del servidor contra instante
 *     del servidor** (la marca de agua, abajo);
 *   · **el apagado por techo** compara **reloj del navegador contra reloj del
 *     navegador** (`ahora`, que el sondeo refresca, contra el instante local en que
 *     se encendió).
 *
 * ⛔ **Nunca uno contra el otro.** Un `Date.now()` local contra un `fecha_hora` del
 * servidor son **dos relojes que nada sincroniza**: un navegador atrasado apagaría
 * el indicador de entrada y uno adelantado no lo apagaría nunca, y **las dos fallas
 * dependen de la máquina del operador y no del árbol**, así que no hay examen que
 * las agarre.
 *
 * ⛔ **NO HAY NINGÚN TEMPORIZADOR NUEVO.** El vencimiento **se deriva en el render**
 * contra `ahora`, que ya se refresca en cada tic del sondeo. Agendarlo con un
 * `setTimeout` no adelantaría ninguna información —la respuesta sólo se conoce
 * cuando el sondeo la trae— y sólo adelantaría el borrado.
 *
 * ⛔ **Y NO DESHABILITA NADA.** Mandar otro mensaje con un turno en curso es un
 * gesto legítimo y sigue disponible: `puedeMandar` no lo mira. Es la doctrina que
 * esta pantalla ya tiene escrita en `selector-participante.tsx`: *«el botón no se
 * deshabilita: informar ≠ bloquear»*.
 */
type EstadoDelTurno = "en-curso" | "sin-noticias"

/**
 * Lo que la pantalla anota cuando un envío vuelve `encolado`.
 *
 * ⛔⛔ **`encolado` Y NADA MÁS LO ENCIENDE, Y ES UN DATO QUE ESCRIBIÓ LA BASE.**
 * `DESENLACE_ENCOLADO` es, textual en `hilo.tsx`, *«el único de los ocho que ejecuta
 * el turno»* ⇒ **con los otros siete no hay ningún trabajo en curso**, y dibujar el
 * indicador sobre ellos sería inventar un trabajo que nadie está haciendo: la
 * obligación fingida cometida en el primer segundo. Y como `encolado` salió del
 * cuerpo del `200`, o sea **del cable**, el indicador no afirma nada nuevo: muestra
 * la consecuencia de algo que la base ya dijo.
 *
 * ⛔⛔ **Y NO SE CUELGA DE `enviando`, QUE ES LO QUE VA A ESCRIBIR CUALQUIERA QUE
 * BUSQUE «EL ESTADO DE EN VUELO QUE YA EXISTE» — PORQUE EXISTE Y SE LLAMA ASÍ.**
 * `setEnviando(false)` vive en el `finally` de `mandar`, o sea que **se apaga en el
 * instante exacto en que el desenlace vuelve y el turno arranca de verdad**. Un
 * indicador atado a `enviando` parpadearía durante el `POST` y desaparecería justo
 * cuando empieza lo que venía a mostrar: mostraría **lo único que NO es «está
 * pensando»**.
 *
 * ⛔ **Y tampoco lo enciende el apretón del botón.** Eso es la burbuja optimista con
 * otra ropa, y esta pantalla ya la rechazó por escrito en `mandar`.
 */
interface TurnoAnotado {
  /**
   * ⛔ El instante **del navegador** en que volvió el `encolado`. Se compara sólo
   * contra `ahora`, que también es del navegador. Ver los dos relojes, arriba.
   */
  readonly desde: number
  /**
   * ⭐⭐ **LA MARCA DE AGUA: el instante del servidor más nuevo de todas las
   * salientes que la pantalla YA TENÍA al mandar, de las DOS fuentes.**
   *
   * ⛔⛔ **La condición ingenua —«el hilo ganó una saliente»— está MEDIDA COMO
   * ROTA**, y por un hallazgo de auditoría del dueño usando el producto: al enviar,
   * **se redibuja la respuesta ANTERIOR del bot** —*«envío → aparece la respuesta
   * vieja → llega la respuesta nueva»*— ⇒ con la condición ingenua el indicador se
   * apagaría **a los ~0 s en el caso real que el dueño reporta**, todas las veces.
   *
   * ⭐ **Y el mismo hallazgo aporta lo que hace funcionar la marca de agua**,
   * textual: *«la burbuja repetida conserva la marca de tiempo original»* ⇒ **no
   * cruza la marca, y el indicador sobrevive al redibujado.**
   *
   * ⚠️ **Esto convive con `AW55` sin depender de él**, y es deliberado: `AW55`
   * repara ese redibujado, y la marca de agua es correcta **con y sin** esa
   * reparación. ⛔ Si alguien la «simplifica» a «creció el hilo» ahora que `AW55`
   * cerró, va a andar en el árbol reparado **y va a volver a romperse** el día que
   * la duplicación reaparezca, esta vez sin hallazgo que lo explique.
   *
   * ⛔ **Y no se compara contra el instante del propio entrante, porque la pantalla
   * NO LO TIENE**: el `POST /entrada` no devuelve ni el `id_sesion` ni el instante
   * que el motor le puso.
   */
  readonly marcaDeAgua: number
}

/**
 * El instante del servidor **más nuevo** de todas las salientes a la vista, de las
 * dos fuentes: las del sondeo (`hilo`, `clase: "saliente"`) y las del historial
 * (`antecedentes`, `es_sistema`).
 *
 * ⚠️ **Un instante ilegible se SALTEA.** `Date.parse` de algo que no es una fecha da
 * `NaN`, y cualquier comparación con `NaN` es `false`: sin saltearlo, una sola fila
 * con la fecha rota dejaría la marca en `NaN` y **ninguna respuesta podría cruzarla
 * nunca**. Salteándola, lo peor que pasa es que esa fila no cuente para el apagado
 * — y el techo sigue estando, que es la garantía que no depende de ningún dato.
 *
 * ⛔ **Sin ninguna saliente devuelve `-Infinity`, no `0` ni `Date.now()`.** Con
 * `-Infinity` **cualquier** respuesta legible cruza la marca, que es lo correcto
 * cuando la pantalla no tenía nada; con `0` pasaría lo mismo por casualidad, y con
 * un reloj local se estaría cruzando los dos relojes, que es lo prohibido.
 */
function instanteMasNuevoDeLasSalientes(
  enElHilo: readonly EnElHilo[],
  delHistorial: readonly LineaDeSesion[],
): number {
  let maximo = Number.NEGATIVE_INFINITY
  const considerar = (iso: string): void => {
    const instante = Date.parse(iso)
    if (!Number.isNaN(instante) && instante > maximo) maximo = instante
  }
  for (const linea of enElHilo) {
    if (linea.clase === "saliente") considerar(linea.fecha_hora)
  }
  for (const linea of delHistorial) {
    if (linea.es_sistema) considerar(linea.fecha_hora)
  }
  return maximo
}

/**
 * ⭐⭐ **EL ESTADO DEL INDICADOR, DERIVADO. `null` = no se dibuja NADA.**
 *
 * Las cuatro salidas, en el orden en que se preguntan:
 *
 *   ① **sin `esperaMaximaMs` no se dibuja nada** —ni indicador, ni aviso, ni un
 *      cartel de que falta configurar algo—. Es la compuerta de `ADR-0040 §④`: sin
 *      configuración, el comportamiento es idéntico al de antes de esta etapa. ⛔ Se
 *      pregunta PRIMERA, y a propósito: así no hay ninguna rama en la que el techo
 *      ausente pueda producir un dibujo;
 *   ② **sin turno anotado** —nunca se mandó nada, o el último envío no encoló— no
 *      hay nada que mostrar;
 *   ③ **si alguna saliente cruzó la marca de agua, se apaga.** Acá entra también el
 *      aviso de espera del motor —«dame un segundo…»— cuando la instancia lo tiene
 *      encendido: llega como **una burbuja más del bot, con un instante nuevo**, así
 *      que cruza la marca y apaga el indicador **por la regla general y sin ningún
 *      caso especial**. ⚠️ Mantenerlo prendido después de ese aviso **es imposible,
 *      no inconveniente**: la entrega no lleva ningún campo que diga qué clase de
 *      saliente es, y `fuentes_consultadas: 0` no discrimina porque el mismo objeto
 *      sirve para cualquier texto fijo. ⭐ Y además es lo correcto: lo que el bot
 *      dijo por el cable le gana a lo que la pantalla supone. ⚠️ **El costo va
 *      escrito y no barrido:** el turno sigue vivo después de ese aviso y la
 *      pantalla no va a mostrar nada hasta la respuesta final. Eso es la pantalla
 *      **dejando de afirmar algo que es cierto** —una sub-afirmación—, que
 *      `ADR-0040 §②` permite y es el lado correcto del error. ⛔ Y no se cierra
 *      estirando el techo «para cubrir lo que viene después del aviso»: eso
 *      reintroduce exactamente el caso prohibido;
 *   ④ **pasada la espera máxima, deja de decir «en curso» y lo dice.**
 *
 * ⛔ **El indicador es un BOOLEANO, no una medición**: usa el paso del tiempo para
 * decidir si se muestra, y **no lo publica**. Nada de «pensando hace 7 s», ni cuenta
 * regresiva, ni barra con porcentaje. Un número acá mediría **la UI y no el bot**
 * —el simulador sondea, así que un cronómetro del navegador se redondea hacia arriba
 * al próximo tic— y quedaría **al lado** del número correcto que `AW57` dibuja en la
 * burbuja: dos números distintos sobre la misma espera, y el falso más grande.
 */
function estadoDelTurno({
  turno,
  esperaMaximaMs,
  instanteMasNuevo,
  ahora,
}: {
  readonly turno: TurnoAnotado | null
  readonly esperaMaximaMs: number | undefined
  readonly instanteMasNuevo: number
  readonly ahora: Date
}): EstadoDelTurno | null {
  if (esperaMaximaMs === undefined) return null
  if (turno === null) return null
  if (instanteMasNuevo > turno.marcaDeAgua) return null
  return ahora.getTime() - turno.desde >= esperaMaximaMs ? "sin-noticias" : "en-curso"
}

/**
 * ⛔⛔ **LO QUE DICE AL VENCER ES UNA FRASE SOBRE LA PANTALLA, NUNCA SOBRE EL
 * MOTOR.** Dice **las dos cosas que la pantalla SÍ sabe**: ① que dejó de mostrar el
 * turno como en curso porque se pasó de la espera máxima configurada, y ② que el
 * sondeo sigue corriendo y que si algo llega va a aparecer en el hilo — lo cual es
 * **literalmente cierto**: el `setInterval` de este archivo no se apaga mientras la
 * pantalla esté montada.
 *
 * ⛔⛔ **PROHIBIDO, POR SU NOMBRE, PORQUE ES LO QUE ALGUIEN VA A ESCRIBIR:** «el bot
 * no contestó», «no hubo respuesta», «se perdió», «falló», «el motor no respondió»,
 * «timeout» y «tiempo de espera agotado». **Las siete afirman algo sobre el MOTOR, y
 * la pantalla no lo sabe**: no puede distinguir un turno muerto de una respuesta que
 * todavía no vio. Es la misma disciplina que los rótulos de familia de `hilo.tsx`
 * —*«llamarlos “rechazados” sería una segunda opinión sobre lo que el motor ya
 * dijo»*— aplicada al caso en que el motor **no dijo nada**.
 *
 * ⚠️ **Y el lado caro del error está medido en palabras:** un operador que reporta
 * «el bot no contesta» manda a buscar el defecto **al motor, a la base y al ruteo**,
 * cuando lo único que pasó fue que la pantalla dejó de esperar.
 *
 * ⛔ Y ninguno de los dos textos cita el contenido de ningún mensaje ni el
 * `destinatario`, que es el teléfono en claro (§④).
 */
const AL_PASARSE_LA_ESPERA =
  "Se pasó la espera máxima configurada para esta pantalla, así que dejó de mostrarse el turno como en curso. El sondeo sigue corriendo: si algo llega, va a aparecer en el hilo."
/**
 * ⭐⭐ RASTRO · `ADR-0292` / `B22` · 16/9/2026 — **EL REGISTRO INTERNO DE TURNOS SE
 * MUESTRA ACÁ. Nada de lo de arriba se toca.**
 *
 * QUÉ: por cada respuesta del motor que llega por el sondeo se busca su fila de
 * `conversation.turn_log` —camino, milisegundos, fragmentos, puntaje del juez— y se
 * dibuja debajo de la burbuja; y al pie va **el último turno del BRAZO**.
 *
 * POR QUÉ: `ADR-0292` abrió `GET /turnos` y el bloqueante `B22` de QA fija la otra
 * mitad, textual: *«endpoints de sólo lectura, **y además el Simulador los muestra**
 * — un acceso que sólo consume el runner es un acceso que nadie mira»*.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ LA CORRELACIÓN ES POR BRAZO Y TIEMPO — Y EL LÍMITE VA ESCRITO, NO TAPADO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **`turn_log` no tiene columna de participante ni de conversación** ⇒ no hay llave
 * natural entre una entrega y su turno. Es el mismo modo de falla que `AW41` ya
 * declaró para el emparejamiento por contenido, en otro cable: *«no hay llave natural
 * entre los dos lados»*.
 *
 * ⇒ lo que se hace, y no más que eso:
 *
 *   ① al montar, **UNA** puesta al día: se pagina `desde = 0` hasta `hayMas === false`
 *     para conocer el `seq` más alto del brazo. ⛔ **No se resuelve con
 *     `desde=0&limite=1`**: el registro ordena ASCENDENTE, así que eso trae el turno
 *     **MÁS VIEJO**. `[medido 16/9/2026]` ~1.000 filas en el registro, 287 del brazo
 *     de pruebas ⇒ **una sola página de 500**, dos o tres en el peor caso;
 *   ② en cada sondeo de `/entregas` —el MISMO, sin un segundo reloj— se pide
 *     `/turnos?desde=‹seq conocido›` y lo que vuelve son **turnos nuevos**;
 *   ③ a cada entrega nueva se le adjudica el turno nuevo cuya `fecha_hora` está
 *     **más cerca** de la suya, dentro de `VENTANA_DE_CORRELACION_MS`. Se consume de
 *     a uno para no pegarle el mismo turno a dos entregas.
 *
 * ⚠️ **DÓNDE ES EXACTO Y DÓNDE NO.** Con **un solo participante activo sobre el
 * brazo** —que es el caso del Simulador: se conversa con uno a la vez— la
 * adjudicación es exacta. Con dos operadores simulando a la vez contra el mismo brazo,
 * o con tráfico real entrando por el canal mientras alguien simula, **dos turnos del
 * mismo segundo se pueden cruzar** y la línea de metadatos quedaría pegada a la
 * burbuja equivocada. **Hueco declarado, dueño `apps-integrator`**: lo cerraría que
 * `turn_log` llevara `id_conversacion`, que hoy no lleva.
 *
 * ⛔ **Y mientras no llegó, se dice «pendiente…» y no se rellena.** La fila es
 * asíncrona —llega 1-3 s después de la entrega— y un `0 ms` de relleno sería una
 * confirmación de la pantalla sobre el único número que un QA mira para decir cuánto
 * tarda el bot: exactamente lo que `ADR-0040 §②` prohibe.
 */
const VENTANA_DE_CORRELACION_MS = 15_000

/**
 * El tope de vueltas de la puesta al día inicial.
 *
 * ⛔ **Existe para que un `hayMas` que nunca baje no cuelgue la pantalla en un bucle
 * de pedidos.** `[medido 16/9/2026]` el registro entero entra en UNA página de 500 en
 * el brazo de pruebas; veinte vueltas son 10.000 filas, dos órdenes por arriba de lo
 * que hay. Si alguna vez se alcanza, lo que queda mal es el `seq` conocido —y el
 * próximo sondeo sigue desde donde llegó—, no el chat.
 */
const TOPE_DE_PAGINAS_DE_TURNOS = 20

/**
 * Cuántos turnos sin dueño se guardan esperando una entrega.
 *
 * ⛔ Un tope y no una lista libre: los turnos del brazo que no son de esta
 * conversación **nunca** van a encontrar entrega, y sin tope la pestaña los acumula
 * mientras esté abierta. Se descartan los más viejos, que son los que menos pueden
 * caer dentro de la ventana de correlación de una entrega que todavía no llegó.
 */
const TOPE_DE_TURNOS_EN_ESPERA = 200

/**
 * ⭐ `B22` · El motivo **corto** de que el registro no esté, para el rótulo que va
 * debajo de una burbuja.
 *
 * ⛔ **No se traduce el texto del backend ni se lo reescribe** —⟦mensaje-de-error⟧ es
 * claro: este lado transporta—. Lo que se hace es elegir QUÉ mostrar en un rótulo de
 * una línea: el código HTTP, que es lo que distingue los tres casos que el servicio
 * declara —`409` canal de otro brazo, `404` canal que no rutea, `400` parámetros— y
 * es lo primero que alguien lleva a un ticket. El mensaje largo no se pierde: no había
 * dónde ponerlo debajo de una burbuja sin empujar el hilo, y el código alcanza para
 * saber a quién preguntarle.
 */
function motivoCorto(causa: unknown): string {
  if (causa instanceof Error) {
    if ("statusCode" in causa) {
      const estado = causa.statusCode
      if (typeof estado === "number" && estado > 0) return `HTTP ${String(estado)}`
    }
    return "no se pudo hablar con bot-web"
  }
  return String(causa)
}

/** Los milisegundos del último turno, con el separador de miles de `es-CL`. */
const FORMATO_MS = new Intl.NumberFormat("es-CL")

export function PantallaSimulador({
  borde,
  bordePadron,
  bordeConversaciones,
  bordeEnvios,
  cargarPlantillas,
}: {
  readonly borde: BordeSimulador
  /**
   * ⭐ `AW18` · El borde del PADRÓN, para elegir a quién se impersona.
   *
   * ⛔ Va por prop y no por contexto, **igual que `borde`**: es lo que un examen
   * tiene que poder reemplazar por un doble. Y **se REUSA el que `App.tsx` ya
   * construye** —no se crea uno nuevo acá—: un segundo cable contra el padrón sería
   * una segunda declaración de los mismos parámetros de query.
   */
  readonly bordePadron: BordePadron
  /**
   * ⭐⭐ `AW28` · El borde de CONVERSACIONES, para ver la sesión más reciente del
   * participante en vez de una pantalla vacía que se lee como «nunca habló».
   *
   * ⛔⛔ **SE REUSA `borde-conversaciones.ts`; NO SE ESTRENA UN BORDE.** Es
   * literalmente el mismo argumento con el que `AW18` reusó `borde-padron.ts`: un
   * `borde-conversaciones-del-simulador.ts` «porque el de conversaciones trae
   * paginación y filtros que no necesito» **duplica la declaración del cable** —los
   * nombres de los parámetros de query, el formato de la página, el revival de
   * fechas—, y el día que `admin-web` renombre uno **la pantalla de conversaciones
   * se rompe y ésta sigue compilando y deja de andar**.
   *
   * ⚠️ **Hueco declarado:** `gates/simulador-conversacional.spec.ts:124-149` (`J1`)
   * existe para hacer cumplir eso, pero **hoy sólo mira el padrón** —su sumidero es
   * un `fetch` cuya URL nombra el padrón—, así que un borde nuevo de conversaciones
   * **no lo pondría rojo**. Es el mismo defecto que `J1` existe para prevenir, sin
   * instrumento que lo agarre.
   *
   * ⚠️⛔ **ES UN SEGUNDO INTERLOCUTOR Y SE ACEPTA A OJOS ABIERTOS.** El simulador le
   * habla a `bot-web` **sin auth**; esto le habla a `admin-web` **con la cookie de
   * Better Auth** (`credentials: "include"`). ⇒ la pantalla pasa a depender de que el
   * operador esté autenticado contra `admin-web`, y **cuando no lo esté LO DICE**, en
   * vez de dibujar un hilo vacío que se lee como «no hay conversación».
   *
   * ⛔ **Va por prop y no por contexto**, igual que `borde` y `bordePadron`: es lo que
   * un examen tiene que poder reemplazar por un doble.
   */
  readonly bordeConversaciones: BordeConversaciones
  /**
   * ⭐ `AW24` · El borde de ENVÍOS, para la columna de plantilla.
   *
   * ⛔ **Opcional, y ausente ⇒ la columna NO se monta.** Es la misma compuerta que
   * `botWebUrl` para la pantalla entera: la columna **escribe un envío real en el
   * estudio**, así que una instancia que no la quiera no la tiene, y no se monta
   * deshabilitada ni con un cartel.
   */
  readonly bordeEnvios?: BordeEnvios
  /**
   * ⭐⭐ `AW46` · El catálogo de plantillas del brazo para el selector de la
   * columna de plantilla.
   *
   * ⛔ **Va por PROP y con default vacío**, igual que los otros bordes de esta
   * pantalla: es lo que un examen tiene que poder reemplazar por un doble sin
   * montar ningún proveedor. Se pasa DERECHO a `ColumnaDePlantilla`, que es quien
   * filtra por estado — a la vista y en su sitio.
   */
  readonly cargarPlantillas?: CargarPlantillas
}): JSX.Element {
  const config = useConfiguracion()

  /**
   * El canal receptor. De acá deriva `bot-web` el brazo, y de ningún otro lado.
   *
   * ⛔⛔ **El default sale de `config.canales[0]`, NUNCA de un literal.** `AW23`:
   * los nombres los mapea `F7_CHANNELS` **en el entorno de `bot-web`**, y quien
   * despliega elige cómo se llaman. Un literal anda en el ambiente de quien lo
   * escribió y deja la otra instancia apuntando a un canal **que no existe**: el
   * motor contesta `canal-no-ruteable`, y eso **se lee como un defecto del ruteo**.
   */
  const [canal, setCanal] = useState(() => config.canales?.[0] ?? "")

  /** ⭐ `AW18` · Quién se impersona, elegido del padrón. */
  const [elegido, setElegido] = useState<Elegido>(null)
  /**
   * ⛔ El escape: la dirección a mano. **No se borra.** Es el único camino para
   * producir `emisor-desconocido` a pedido, que es uno de los ocho desenlaces y de
   * los que un operador va a estar buscando cuando el simulador «no anda». Si el
   * selector fuera el único camino, ese desenlace quedaría **inalcanzable desde la
   * pantalla** y la etapa habría reducido el instrumento mientras lo hacía cómodo.
   */
  const [aMano, setAMano] = useState("")

  /** El canal tipeado, cuando se eligió el escape de la lista. */
  const [canalAMano, setCanalAMano] = useState("")

  const canales = config.canales ?? []

  const [hilo, setHilo] = useState<readonly EnElHilo[]>([])
  const [enviando, setEnviando] = useState(false)
  const [errorDeLectura, setErrorDeLectura] = useState<string | null>(null)

  /**
   * ⭐⭐ `AW59` · El turno que la base dijo que encoló, con su marca de agua.
   * `null` = no hay ninguno anotado. Ver `TurnoAnotado` y `estadoDelTurno`.
   *
   * ⛔ **Los dos datos van JUNTOS en un solo estado y no en dos**: la marca de agua
   * sin el instante de encendido no sirve para nada, y dos `useState` separados se
   * pueden actualizar en órdenes distintos y dejar un turno con la marca del
   * anterior — que apagaría el indicador antes de encenderlo.
   */
  const [turno, setTurno] = useState<TurnoAnotado | null>(null)

  /**
   * ⛔ El reloj con el que se deriva el estado de la ventana. Vive acá y **entra por
   * prop** a `Hilo`: `sesiones.ts` no lo lee de ningún lado. Se refresca con el
   * mismo pulso que el sondeo, así que el cartel envejece con la pantalla en vez de
   * quedar congelado en el primer render.
   */
  const [ahora, setAhora] = useState(() => new Date())

  /**
   * ⭐ El lienzo, para poder llevarlo al mensaje más nuevo. Es la afordancia
   * básica de un cliente de mensajería y la única que no se puede simular con
   * CSS: sin esto, una conversación con doscientas líneas se abre en la PRIMERA
   * y el operador tiene que rodar hasta abajo para ver lo que acaba de mandar.
   */
  const lienzo = useRef<HTMLDivElement | null>(null)

  const cursor = useRef(CURSOR_INICIAL)
  const proximoId = useRef(0)
  /** ⭐ `AW39` · La primera lectura de `/entregas` de esta identidad sólo adelanta el cursor. */
  const primeraLectura = useRef(true)

  /**
   * ⭐⭐ `ADR-0292` / `B22` · **EL ESTADO DEL REGISTRO DE TURNOS.** Ver el bloque del
   * encabezado de este archivo para la correlación y su límite.
   */

  /**
   * El `seq` más alto del brazo que esta pestaña ya vio. `null` = **nunca se leyó**, y
   * entonces el próximo sondeo hace la puesta al día paginada.
   *
   * ⛔ **Es del BRAZO, no del participante** —igual que el `cursor` de `/entregas`—, y
   * por eso se reinicia cuando cambia el CANAL y no cuando cambia la identidad: volver
   * a paginar 287 filas por elegir a otra persona del mismo brazo no aporta nada.
   */
  const seqConocido = useRef<number | null>(null)
  /**
   * ⛔⛔ **EL CERROJO DE LA PUESTA AL DÍA ES UN `ref`, por lo mismo que `AW39`.** La
   * puesta al día son varios `await` seguidos; si el sondeo vuelve a entrar en el medio
   * lee `seqConocido.current` todavía en `null` y la pagina DOS veces. Un `ref` se lee y
   * se escribe en el acto —ahí está medido el defecto gemelo: *«traía DOS páginas por
   * gesto»—.
   */
  const poniendoseAlDia = useRef(false)
  /** Las salientes dibujadas que todavía esperan su fila del registro, con su instante. */
  const salientesSinRegistro = useRef<{ readonly id: number; readonly ms: number }[]>([])
  /** Los turnos nuevos que todavía no encontraron entrega. Acotados, ver el tope. */
  const turnosSinDuenio = useRef<TurnoEnLaRed[]>([])
  /** Qué turno le tocó a cada línea saliente. La llave es el `id` de la línea del hilo. */
  const [registros, setRegistros] = useState<ReadonlyMap<number, TurnoEnLaRed>>(() => new Map())
  /**
   * ⭐ El último turno del BRAZO. Sirve **aunque la correlación falle**: no depende de
   * emparejar nada, sale derecho del registro.
   */
  const [ultimoTurno, setUltimoTurno] = useState<TurnoEnLaRed | null>(null)
  /** El motivo corto de que `/turnos` no conteste, o `null`. Ver `motivoCorto`. */
  const [errorDeTurnos, setErrorDeTurnos] = useState<string | null>(null)

  /**
   * ⭐⭐ `AW28` · La sesión MÁS RECIENTE del participante, leída de
   * `GET conversaciones`. `null` = todavía no se leyó nada para esta identidad.
   *
   * ⛔ **«La más reciente», no «la única».** Medido el 28/8/2026: la invariante «una
   * sola sesión activa por participante» **NO está impuesta** —`sessions` no tiene
   * columna de estado ni índice único, y `resolverSesion` (`ventana.ts:149-176`) hace
   * `SELECT`+`INSERT` sin bloqueo—, así que la pantalla dice lo que puede afirmar.
   */
  const [antecedentes, setAntecedentes] = useState<readonly LineaDeSesion[]>([])
  const [errorDeSesion, setErrorDeSesion] = useState<string | null>(null)
  const [leyendoSesion, setLeyendoSesion] = useState(false)

  /**
   * ⭐⭐ `AW39` · **LA PAGINACIÓN HACIA ARRIBA.** Pedido del dueño, textual: *«carga
   * los últimos mensajes y luego si scrolleo para arriba voy paginando como especie
   * de paginación infinita»*.
   *
   * ⛔ **Cero contrato nuevo, y está MEDIDO contra el servicio vivo** —no contra
   * `fake.ts`—: `GET /conversaciones` respeta `page`/`pageSize` con páginas contiguas
   * y sin solape (`page=1..3` con `pageSize=3` sobre 452 filas, orden descendente
   * continuo).
   */
  const paginaLeida = useRef(1)
  const [totalEnBase, setTotalEnBase] = useState(0)
  const [cargandoMas, setCargandoMas] = useState(false)
  /**
   * ⛔⛔ `AW39` · **EL CERROJO ES UN `ref`, NO EL `useState`, Y ESTÁ MEDIDO.**
   *
   * Con el cerrojo puesto sólo en `cargandoMas` la pantalla traía **DOS páginas por
   * gesto** (medido en Chrome: 10 → 30 → 50 → 70 burbujas, de a 20 con `pageSize` 10).
   * La causa es la clausura: un rodado dispara varios eventos en el mismo turno y
   * todos leen el `cargandoMas` **viejo**, que todavía es `false`. Un `ref` se lee y
   * se escribe en el acto. El `useState` queda **sólo para dibujar el cartel**.
   */
  const cargando = useRef(false)
  /**
   * ⛔ El alto del lienzo ANTES de meter las líneas viejas arriba. Sin esto el
   * navegador conserva `scrollTop` y **el contenido salta**: el operador estaba
   * leyendo una línea y de golpe está mirando otra. Ver el `useLayoutEffect`.
   */
  const altoAntesDePegar = useRef<number | null>(null)
  /**
   * ⭐⭐ `AW40` · **¿Estaba mirando el final?** Lo que decide si un REDIMENSIONADO
   * tiene derecho a mover el hilo. Ver el `ResizeObserver` más abajo.
   *
   * ⛔ Se anota al rodar y no se calcula en el momento del redimensionado: cuando el
   * observador avisa, **el reflow YA ocurrió** y `scrollHeight` es el nuevo, así que
   * ahí ya no se puede saber dónde estaba parado el operador.
   */
  const estabaAlFondo = useRef(true)
  /**
   * El pulso que fuerza a releer la sesión. Sube después de CADA envío.
   *
   * ⛔ Sin esto el anclaje de `AW29` quedaría viejo justo cuando cambia: **un
   * entrante del operador SÍ mueve el anclaje** (`ventana.ts:122`,
   * `ruedaElAnclaje: !esSistema`), y el `POST /entrada` devuelve `{ outcome }` y nada
   * más — ni el `id_sesion` ni el instante que el motor le puso.
   *
   * ⚠️ **Y lo que sigue sin cerrar, dicho:** entre relecturas el anclaje es el del
   * último `GET conversaciones`. Un mensaje escrito desde otra superficie no lo mueve
   * hasta el próximo envío o el próximo cambio de identidad.
   */
  const [pulsoDeSesion, setPulsoDeSesion] = useState(0)

  const canalListo = (canal === CANAL_A_MANO ? canalAMano : canal).trim()
  /**
   * ⛔⛔ `AW26` · **LO QUE VIAJA COMO `de` ES LA DIRECCIÓN, NUNCA EL `id`.**
   * `POST /entrada` simula el webhook de Meta, **donde el emisor ES una dirección**
   * (`ADR-0232`). Mandar el `id` —que la pantalla tiene a mano y es el que aparece en
   * `F7_PARTICIPANTS`— haría al simulador **menos fiel al canal real**, que es
   * exactamente lo que `ADR-0196` dice que el simulador existe para evitar: el día
   * que se conecte Meta de verdad, **el camino probado no sería el que corre**.
   */
  const participanteListo = elegido !== null ? elegido.direccion : aMano.trim()
  const conversacionArmada = canalListo !== "" && participanteListo !== ""

  /**
   * ⛔⛔ **CAMBIAR DE IDENTIDAD LIMPIA EL HILO Y REINICIA EL CURSOR.**
   *
   * Sin esto, las burbujas del participante anterior **se quedan en pantalla debajo
   * de las del nuevo**, y el operador lee una conversación que nunca existió. El
   * cursor se reinicia porque es **el cursor del brazo**, no el de una conversación.
   */
  useEffect(() => {
    cursor.current = CURSOR_INICIAL
    primeraLectura.current = true
    paginaLeida.current = 1
    setHilo([])
    setErrorDeLectura(null)
    /*
     * ⭐ `AW59` · **Y el turno anotado también se va.** Es del participante
     * anterior: su marca de agua se tomó sobre un hilo que ya no está en pantalla, y
     * dejarlo puesto mostraría un turno en curso sobre una conversación en la que no
     * se mandó nada.
     */
    setTurno(null)
    /*
     * ⭐ `ADR-0292` / `B22` · **Y con el hilo se va lo que colgaba de él.** Los
     * `registros` están indexados por el `id` de una línea que ya no existe, y las
     * salientes en espera son de una conversación que se dejó de mirar: arrastrarlas
     * haría que el primer turno del participante nuevo se adjudique a una entrega del
     * anterior, que es justo el cruce que la ventana de tiempo existe para evitar.
     * ⛔ El `seq` conocido NO se reinicia acá: es del brazo. Ver el efecto de abajo.
     */
    salientesSinRegistro.current = []
    turnosSinDuenio.current = []
    setRegistros(new Map())
  }, [canalListo, participanteListo])

  /**
   * ⭐⭐ `ADR-0292` / `B22` · **CAMBIAR DE CANAL ES CAMBIAR DE BRAZO, Y EL REGISTRO ES
   * DEL BRAZO.**
   *
   * ⛔ Sin esto, el `seq` del brazo anterior se usaría como `desde` contra el nuevo: el
   * registro del otro brazo tiene su propia numeración, así que el sondeo o traería
   * filas que ya pasaron o **se saltearía todas las nuevas sin que nadie se entere**.
   * Y un canal que es de otro brazo contesta `409`, que es lo que el rótulo dice.
   */
  useEffect(() => {
    seqConocido.current = null
    setUltimoTurno(null)
    setErrorDeTurnos(null)
  }, [canalListo])

  /**
   * ⭐⭐ `AW28` · **LA SESIÓN MÁS RECIENTE, AL ELEGIR PARTICIPANTE.**
   *
   * El defecto que cierra: el hilo es estado local que **se vacía** al cambiar de
   * identidad, y sólo se rellena con lo que el operador manda **en esta pestaña** más
   * lo que el sondeo trae **desde el cursor**, que también se reinicia. ⇒ el operador
   * elegía a alguien con una sesión abierta hace dos horas **y veía una pantalla
   * vacía**.
   *
   * ⭐ **Cero contrato nuevo.** `borde-conversaciones.ts:126-128` ya filtra por
   * participante y `FilaDeMensaje` ya trae `id_sesion`, `fecha_hora`, `es_sistema` y
   * `posicion_en_sesion`. **La sesión vigente es el `id_sesion` de la fila más
   * nueva**, porque el orden por `fecha_hora` descendente es invariante y sin
   * parámetro (⟦sin-parametro-de-orden⟧).
   *
   * ⚠️ **Lo que esto NO trae, y no se tapa:** se pide UNA página, así que de una
   * sesión más larga que `pageSize` se ve el tramo más nuevo. Decir «la sesión
   * entera» requeriría paginar hacia atrás, y eso es una decisión de producto que
   * esta etapa no toma.
   */
  /**
   * ⛔ `R3-SAN-UI` · ver ⟦el-tamano-de-pagina-es-uno-de-CINCO⟧ en
   * `conversaciones/borde-conversaciones.ts`. `R3-CCV1` cerró el tamaño a cinco
   * valores y `config.pageSize` es un `number` de la configuración de la
   * instancia; `null` = la instancia pidió uno que el borde contestaría con 4xx.
   * No se sustituye por 10 en silencio, y quien lo saca de `config.pageSize` es
   * `R3-UI1`.
   */
  const tamanoDePagina = tamanoDePaginaDelContrato(config.pageSize)

  useEffect(() => {
    if (participanteListo === "") {
      setAntecedentes([])
      setErrorDeSesion(null)
      return undefined
    }

    if (tamanoDePagina === null) {
      // Falla cerrada por el camino de error que el Simulador YA tiene.
      setAntecedentes([])
      setLeyendoSesion(false)
      setErrorDeSesion(TAMANO_DE_PAGINA_FUERA_DE_ESCALA)
      return undefined
    }

    let vivo = true
    setLeyendoSesion(true)

    void bordeConversaciones
      .listar({
        // ⛔ Igualdad exacta sobre lo que viaja como `de`. El filtro del cable se
        //    llama `telefono` y este valor es una DIRECCIÓN: son el mismo campo
        //    opaco del otro lado (⟦el-telefono-es-opaco⟧), y no se normaliza nada.
        // ⭐ `R3-SAN-UI` · `contenido: null` = «no filtra» (`R3-CCV1`): el
        //    Simulador lee el hilo del participante y nunca filtró por texto.
        filtro: { desde: null, hasta: null, telefono: participanteListo, contenido: null },
        pagina: 1,
        tamanoDePagina,
      })
      .then((pagina) => {
        if (!vivo) return
        paginaLeida.current = 1
        setTotalEnBase(pagina.total)
        /*
         * ⛔⛔ `AW39` · **EL RECORTE A LA SESIÓN VIGENTE SE FUE, Y ES UNA DECISIÓN
         * DEL DUEÑO QUE REVIERTE OTRA ANTERIOR.**
         *
         * `AW28` recortaba a la sesión más reciente, y había un caso que lo afirmaba
         * (`aw26`: *«sólo se muestra la sesión VIGENTE, no todo lo que trajo la
         * página»*). **El pedido nuevo lo contradice de frente**: paginar hacia atrás
         * ES cruzar sesiones. ⇒ el recorte se saca y **el caso de `aw26` se dio
         * vuelta, no se borró**: sigue afirmando algo, ahora lo contrario, con el
         * rastro de por qué.
         *
         * ⭐ Los cortes de sesión NO se pierden: `hilo.tsx` los dibuja donde cambia
         * el `id_sesion`, que es justo lo que ahora se puede ver.
         */
        setAntecedentes(
          [...pagina.items]
            /*
             * ⭐⭐ `AW37` · **AL DERECHO. LA PÁGINA VIENE DEL MÁS NUEVO AL MÁS VIEJO.**
             *
             * ⛔⛔ **Era el defecto, y estaba medido a ojo por el dueño**: *«si refresco
             * una conversación no se ven los mensajes del usuario»*. Se ven —el examen
             * lo afirma— pero **el hilo se leía dado vuelta**, con lo último arriba, y
             * una conversación al revés no se lee: se descifra.
             *
             * `ORDEN_DE_LA_TABLA` (`modules/conversation/src/mensajes/consulta.ts`) es
             * `ORDER BY m.fecha_hora DESC, seq DESC` y **no hay parámetro para pedir
             * otro** (⟦sin-parametro-de-orden⟧). ⇒ la pantalla lo da vuelta al DIBUJAR.
             *
             * ⛔ **Se INVIERTE, no se ORDENA por `fecha_hora`.** Un `sort` por fecha
             * perdería el desempate por `seq` —que `LineaDeSesion` no lleva y este lado
             * no puede reconstruir—, así que dos líneas del mismo instante quedarían en
             * un orden que el motor no eligió. El inverso exacto del orden del contrato
             * **sí** es el orden en que ocurrieron, desempate incluido.
             *
             * ⚠️ **Esto NO reordena la tabla de conversaciones y no la contradice.** Lo
             * que ⟦sin-parametro-de-orden⟧ prohíbe es que una pantalla PAGINADA reordene
             * su página —la 2 quedaría ordenada por otro criterio que la 1—. Acá no hay
             * paginación para el operador: es UNA página, la de la sesión vigente, y es
             * un hilo de chat, no una tabla. Las dos lecturas van en el informe.
             *
             * ⚠️ Y sigue siendo **el tramo MÁS NUEVO** de una sesión más larga que
             * `pageSize`: lo dice la prosa de `Antecedentes`, y darlo vuelta no lo cambia.
             */
            .reverse()
            .map((m) => ({
              id_sesion: m.fila.id_sesion,
              id_conversacion: m.fila.id_conversacion,
              // El cable ya revivió el `Date`; `sesiones.ts` come ISO.
              fecha_hora: m.fila.fecha_hora.toISOString(),
              es_sistema: m.fila.es_sistema,
              contenido: m.fila.contenido,
              posicion_en_sesion: m.fila.posicion_en_sesion,
            })),
        )
        setErrorDeSesion(null)
      })
      .catch((causa: unknown) => {
        if (!vivo) return
        setAntecedentes([])
        setErrorDeSesion(causa instanceof Error ? causa.message : String(causa))
      })
      .finally(() => {
        if (vivo) setLeyendoSesion(false)
      })

    return () => {
      vivo = false
    }
  }, [bordeConversaciones, participanteListo, tamanoDePagina, pulsoDeSesion])

  /**
   * ⭐⭐ `AW39` · **TRAE LA PÁGINA SIGUIENTE Y LA PEGA ARRIBA.**
   *
   * ⛔ **Anota el alto ANTES de pegar.** El navegador conserva `scrollTop` cuando
   * entra contenido arriba, así que sin compensar, el operador que estaba leyendo una
   * línea **de golpe está mirando otra**: es el modo de falla clásico de esta función.
   * La compensación va en el `useLayoutEffect` de abajo, que corre **antes de pintar**.
   */
  async function traerMasViejas(): Promise<void> {
    if (cargando.current || participanteListo === "") return
    if (antecedentes.length >= totalEnBase) return
    // `R3-SAN-UI` · idem: sin un tamaño de los cinco no se pide nada. El aviso ya
    // está puesto por el efecto de arriba, que corre antes que cualquier gesto.
    if (tamanoDePagina === null) return
    cargando.current = true
    setCargandoMas(true)
    altoAntesDePegar.current = lienzo.current?.scrollHeight ?? null
    try {
      const pagina = await bordeConversaciones.listar({
        // ⭐ `R3-SAN-UI` · `contenido: null` = «no filtra» (`R3-CCV1`): el
        //    Simulador lee el hilo del participante y nunca filtró por texto.
        filtro: { desde: null, hasta: null, telefono: participanteListo, contenido: null },
        pagina: paginaLeida.current + 1,
        tamanoDePagina,
      })
      paginaLeida.current = paginaLeida.current + 1
      setTotalEnBase(pagina.total)
      const masViejas = [...pagina.items]
        // Mismo `.reverse()` que la primera página, y por el mismo motivo (`AW37`).
        // ⛔ Sobre una COPIA: `pagina.items` es `readonly` y `.reverse()` muta.
        .reverse()
        .map((m) => ({
          id_sesion: m.fila.id_sesion,
          id_conversacion: m.fila.id_conversacion,
          fecha_hora: m.fila.fecha_hora.toISOString(),
          es_sistema: m.fila.es_sistema,
          contenido: m.fila.contenido,
        }))
      setAntecedentes((previas) => [...masViejas, ...previas])
    } catch (causa) {
      altoAntesDePegar.current = null
      setErrorDeSesion(causa instanceof Error ? causa.message : String(causa))
    } finally {
      cargando.current = false
      setCargandoMas(false)
    }
  }

  /**
   * ⛔⛔ `AW39` · **LA COMPENSACIÓN, ANTES DE PINTAR.** `useLayoutEffect` y no
   * `useEffect`: con `useEffect` el salto **se ve** un cuadro antes de corregirse.
   */
  useLayoutEffect(() => {
    const nodo = lienzo.current
    const antes = altoAntesDePegar.current
    if (nodo === null || antes === null) return
    altoAntesDePegar.current = null
    nodo.scrollTop = nodo.scrollTop + (nodo.scrollHeight - antes)
  }, [antecedentes])

  /**
   * El sondeo. **La pantalla pregunta; nadie le empuja** — `GET /sse` existe y se
   * descarta a propósito: es una superficie más que el navegador tendría que
   * alcanzar, y esta unidad no tiene una sola conexión persistente.
   */
  useEffect(() => {
    if (!conversacionArmada) return undefined

    let vivo = true

    const preguntar = async (): Promise<void> => {
      try {
        const pagina = await borde.entregas(canalListo, participanteListo, cursor.current)
        if (!vivo) return
        cursor.current = pagina.cursor
        setErrorDeLectura(null)
        setAhora(new Date())
        /*
         * ⭐⭐ `AW39` · **LA PRIMERA LECTURA SÓLO ADELANTA EL CURSOR. NO DIBUJA.**
         *
         * ⛔⛔ El pedido del dueño, textual: *«no que se cargue todo porque sobrecarga
         * la pantalla»*. Medido: con `CURSOR_INICIAL = "0"` esta llamada traía **249
         * entregas de doce días y nueve sesiones, TODAS del sistema** —`GET /entregas`
         * viene pre-filtrado a `es_sistema`— y el hilo quedaba en **251 burbujas del
         * Sistema contra 3 del Participante**. Un monólogo, no una conversación.
         *
         * ⭐ **Descartarlas no pierde nada, y está medido:** las 249 son un subconjunto
         * de las 452 filas que `GET /conversaciones` ya trae **con las dos voces**, que
         * es de donde sale el historial paginado. ⇒ el pasado lo cuenta UNA sola fuente
         * y el sondeo queda para lo que llega **mientras el operador mira**.
         *
         * ⭐ Y cuesta **una** llamada: medido, `/entregas` devolvió las 249 en UNA
         * página con `hayMas: false`, así que con un viaje el cursor ya está al día.
         */
        /*
         * ⭐ `ADR-0292` / `B22` · **Los dos `return` de acá abajo se volvieron ramas.**
         * RASTRO 16/9/2026: salían de `preguntar` entera, y la lectura del registro de
         * turnos —que ahora va al final del mismo sondeo— nunca habría corrido en el
         * caso más común, que es «no llegó ninguna entrega». **La lógica de las
         * entregas no cambió ni un paso**: lo de arriba sigue haciendo exactamente lo
         * mismo, sólo que sin cortar el resto del sondeo.
         */
        if (primeraLectura.current) {
          primeraLectura.current = false
        } else if (pagina.entregas.length > 0) {
          const nuevas = pagina.entregas.map((entrega) => ({
            clase: "saliente" as const,
            id: proximoId.current++,
            contenido: entrega.contenido,
            fecha_hora: entrega.fecha_hora,
            id_sesion: entrega.id_sesion,
          }))
          /*
           * ⭐⭐ Cada saliente nueva entra a la cola de correlación **con su instante
           * ya parseado**: es lo único comparable contra la `fecha_hora` de un turno.
           * ⛔ Una fecha ilegible da `NaN`, toda comparación con él es falsa y la línea
           * se queda en «pendiente…» —que es la verdad— en vez de emparejar cualquier
           * cosa.
           */
          salientesSinRegistro.current = [
            ...salientesSinRegistro.current,
            ...nuevas.map((linea) => ({ id: linea.id, ms: Date.parse(linea.fecha_hora) })),
          ]
          setHilo((anterior) => [...anterior, ...nuevas])
        }
      } catch (causa) {
        if (!vivo) return
        /*
         * ⭐⭐ `AW59` · **EL RELOJ AVANZA TAMBIÉN CUANDO EL SONDEO FALLA, Y ESTO NO
         * ES PROLIJIDAD.** `ahora` es contra lo que se deriva el vencimiento del
         * indicador de turno. Si sólo se refrescara en el camino feliz, una red
         * caída dejaría el reloj congelado **y el indicador prendido para siempre**
         * — que es exactamente el caso que `ADR-0040 §②` prohíbe, producido por el
         * único camino en que nadie lo iba a mirar.
         *
         * ⚠️ Y es lo correcto también para el cartel de la ventana de sesión, que ya
         * usaba este reloj: envejecer con la pantalla es lo que se pidió de él; que
         * dejara de envejecer porque un pedido falló nunca fue la intención.
         */
        setAhora(new Date())
        setErrorDeLectura(causa instanceof Error ? causa.message : String(causa))
      }

      /*
       * ⭐⭐ `ADR-0292` / `B22` · **EL REGISTRO SE LEE CON EL MISMO PULSO, Y AFUERA DEL
       * `try` DE LAS ENTREGAS.**
       *
       * ⛔⛔ **Sin segundo reloj, y es una restricción y no una comodidad.** Un
       * `setInterval` propio para `/turnos` sería *«un segundo camino hacia el mismo
       * estado: dos lugares que pueden discrepar»* —la disciplina que `AW1` fijó y que
       * el ⑥ del encabezado ya invocó para descartar `GET /sse`—, y además dejaría al
       * registro llegando en un orden distinto al de las entregas que tiene que
       * emparejar.
       *
       * ⛔⛔ **Y va en SU PROPIO `try`, DESPUÉS del de las entregas.** Si `/turnos`
       * contesta `409`, `404`, `400` o no contesta, **el chat sigue mandando y leyendo
       * entregas**: lo único que se pierde es el renglón de metadatos, que lo dice.
       * `B22` pide mostrar el registro, no condicionar el simulador a él —cambiar la
       * pantalla entera por un endpoint de diagnóstico sería el peor de los dos mundos—.
       */
      if (!vivo) return
      await ponerseAlDiaConElRegistro()
    }

    /**
     * ⭐⭐ La lectura del registro de turnos del brazo. Ver el bloque del encabezado
     * de este archivo: **qué se correlaciona, cómo, y dónde deja de ser exacto.**
     */
    const ponerseAlDiaConElRegistro = async (): Promise<void> => {
      if (poniendoseAlDia.current) return
      poniendoseAlDia.current = true
      try {
        /*
         * ① **LA PUESTA AL DÍA INICIAL, UNA SOLA VEZ POR BRAZO.**
         *
         * ⛔ **No adjudica NINGÚN turno a ninguna entrega, y no es una omisión.** Todo
         * lo que ya estaba escrito cuando esta pestaña abrió es PASADO: una entrega que
         * todavía no llegó no puede haberlo producido. Lo único que esta vuelta deja es
         * el `seq` desde el que mirar, y el último turno para el bloque del pie.
         */
        if (seqConocido.current === null) {
          let desde = 0
          let ultimo: TurnoEnLaRed | null = null
          for (let vuelta = 0; vuelta < TOPE_DE_PAGINAS_DE_TURNOS; vuelta++) {
            const pagina = await borde.leerTurnos({
              canal: canalListo,
              desde,
              limite: LIMITE_DE_TURNOS,
            })
            if (!vivo) return
            desde = pagina.ultimoSeq
            ultimo = pagina.turnos.at(-1) ?? ultimo
            if (!pagina.hayMas) break
          }
          seqConocido.current = desde
          setUltimoTurno(ultimo)
          setErrorDeTurnos(null)
          return
        }

        /* ② De acá en adelante, sólo lo NUEVO: `desde` es exclusivo (ver el borde). */
        const pagina = await borde.leerTurnos({
          canal: canalListo,
          desde: seqConocido.current,
          limite: LIMITE_DE_TURNOS,
        })
        if (!vivo) return
        seqConocido.current = pagina.ultimoSeq
        setErrorDeTurnos(null)
        if (pagina.turnos.length > 0) {
          setUltimoTurno(pagina.turnos.at(-1) ?? null)
          turnosSinDuenio.current = [...turnosSinDuenio.current, ...pagina.turnos].slice(
            -TOPE_DE_TURNOS_EN_ESPERA,
          )
        }
        /*
         * ③ Se empareja SIEMPRE, aunque esta vuelta no haya traído nada: una entrega de
         * hace tres segundos puede estar esperando un turno que llegó en la vuelta
         * anterior y todavía no encontró a nadie.
         */
        emparejar()
      } catch (causa) {
        if (!vivo) return
        setErrorDeTurnos(motivoCorto(causa))
      } finally {
        poniendoseAlDia.current = false
      }
    }

    /**
     * ⭐⭐ **LA ADJUDICACIÓN: EL MÁS CERCANO EN EL TIEMPO, DENTRO DE LA VENTANA.**
     *
     * ⛔ **Se consume de a uno.** Un turno adjudicado sale de la bolsa, así que dos
     * entregas nunca muestran la misma fila —es la misma disciplina que el `usadas` de
     * `AW41`, por el mismo motivo: sin llave natural, lo único que queda es no repetir—.
     *
     * ⛔ **Y el que no entra en la ventana se QUEDA esperando**, no se adjudica al menos
     * malo: un turno a cuarenta segundos de una entrega es de otro mensaje, y ponerlo
     * ahí diría «esta respuesta tardó X» sobre un X que no es de esta respuesta.
     */
    const emparejar = (): void => {
      if (salientesSinRegistro.current.length === 0) return
      if (turnosSinDuenio.current.length === 0) return

      const adjudicados = new Map<number, TurnoEnLaRed>()
      const siguenEsperando: { readonly id: number; readonly ms: number }[] = []
      const libres = [...turnosSinDuenio.current]

      for (const pendiente of salientesSinRegistro.current) {
        let mejor = -1
        let mejorDistancia = Number.POSITIVE_INFINITY
        for (let i = 0; i < libres.length; i++) {
          const candidato = libres[i]
          if (candidato === undefined) continue
          const distancia = Math.abs(Date.parse(candidato.fecha_hora) - pendiente.ms)
          if (distancia <= VENTANA_DE_CORRELACION_MS && distancia < mejorDistancia) {
            mejor = i
            mejorDistancia = distancia
          }
        }
        const elegido = mejor === -1 ? undefined : libres[mejor]
        if (elegido === undefined) {
          siguenEsperando.push(pendiente)
          continue
        }
        libres.splice(mejor, 1)
        adjudicados.set(pendiente.id, elegido)
      }

      salientesSinRegistro.current = siguenEsperando
      turnosSinDuenio.current = libres
      if (adjudicados.size === 0) return
      setRegistros((previos) => new Map([...previos, ...adjudicados]))
    }

    void preguntar()
    const reloj = setInterval(() => {
      void preguntar()
    }, config.refrescoMs)

    return () => {
      vivo = false
      clearInterval(reloj)
    }
  }, [borde, canalListo, participanteListo, conversacionArmada, config.refrescoMs])

  /**
   * ⭐⭐ `AW37` · La huella del final del HISTORIAL, para que el rodado la vea.
   *
   * ⛔ **`antecedentes.length` solo no alcanza.** La relectura pide UNA página de
   * `pageSize`: en una sesión saturada entra una línea nueva y sale la más vieja, y
   * el largo **no cambia**. El instante de la última es lo que sí cambia siempre.
   */
  const ultimoAntecedente = antecedentes.at(-1)?.fecha_hora

  /**
   * Lleva el lienzo al final cuando entra una línea.
   *
   * ⭐⭐ `AW37` · **Y «una línea» incluye las del HISTORIAL, que es lo que faltaba.**
   *
   * ⛔⛔ El defecto que cierra, en palabras del dueño: *«cuando llega el mensaje del
   * sistema no se ve, como que el último mensaje queda oculto»*. El efecto colgaba
   * **sólo de `hilo.length`**, y el hilo vivo no es la única cosa que cambia el alto
   * del lienzo:
   *
   *   · **al CARGAR** una conversación, `hilo.length` vale 0 antes y 0 después —todo
   *     lo que aparece son antecedentes—, así que **no rodaba nunca**: la pantalla se
   *     abría en el cartel de la ventana y los mensajes quedaban abajo del pliegue.
   *     Eso es, literal, «quiero poder cargar la conversación».
   *   · **después de cada gesto**, el `pulso` relee `GET conversaciones` y el bloque
   *     de antecedentes —que va ARRIBA del hilo vivo— **crece**: el fondo se corre
   *     hacia abajo con `hilo.length` quieto, y lo último queda fuera de la vista.
   *
   * ⚠️ **`prefers-reduced-motion` se respeta y no se amputa** (`DESIGN.md` regla
   * 13): quien pide menos movimiento **sigue yendo al mensaje nuevo**, sólo que sin
   * el desplazamiento animado. Apagarlo entero lo dejaría mirando el mensaje
   * equivocado, que es una pérdida de información y no de decoración.
   *
   * ⭐⭐ `AW38` · **Y EL RODADO LARGO NO SE ANIMA — el tramo que `AW37` no vio.**
   *
   * ⛔⛔ `AW37` arregló QUE se pidiera rodar. **Faltaba que el rodado LLEGUE.**
   * Medido en Chrome real el 31/8/2026, con 257 burbujas y 41.587 px de hilo:
   *
   * ```
   * t=3948 ms  contenido dibujado   top=1       falta 41.198
   * t=4001     rueda del operador   top=939     ← el navegador CANCELA el suave
   * t=4102                          top=1.019
   * t=8802     (nunca más se mueve) top=1.019   FALTA 40.179 px
   * ```
   *
   * Sin interrumpir tardaba **1,55 s** en llegar (t=3948 → t=5502). ⇒ hay segundo y
   * medio en el que el operador mira el principio del hilo, y **cualquier gesto suyo
   * en esa ventana lo deja varado arriba, con la última burbuja cortada por el
   * fondo**. Eso es, exacto, la captura que mandó el dueño.
   *
   * ⚠️ **Lo que esto NO hace, y no se tapa:** rueda al fondo SIEMPRE, aunque el
   * operador haya subido a leer algo viejo. Respetar «está leyendo arriba» exige
   * medir `scrollTop` contra `scrollHeight` en cada cambio, y ese umbral es una
   * decisión de producto que esta reparación no toma.
   */
  useEffect(() => {
    const nodo = lienzo.current
    if (nodo === null) return
    /*
     * ⛔⛔ `AW39` · **NO se rueda al fondo cuando lo que entró fue historial VIEJO
     * por arriba.** Sería el peor de los dos mundos: el operador pide ver lo
     * anterior y la pantalla lo devuelve al final de un tirón. La compensación de
     * posición la hace el `useLayoutEffect`, que ya anotó el alto previo.
     */
    if (altoAntesDePegar.current !== null) return
    const quiereMenos = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    /*
     * ⭐⭐ `AW38` · **UN SALTO LARGO NO SE ANIMA. Y no es gusto: está MEDIDO.**
     *
     * ⛔⛔ Ver el encabezado del efecto. `smooth` sobre 41.198 px tarda **1,55 s**, y
     * el navegador CANCELA un rodado suave ante el primer gesto del operador —que es
     * lo correcto de su parte—. Medido: una sola rueda a los 4.001 ms lo dejó varado
     * en 1.019 px, **a 40.179 px del último mensaje**, para siempre.
     *
     * ⇒ si falta más de UNA pantalla, esto no es un deslizamiento: es una
     * **reubicación**, y se hace de una. `smooth` queda para lo que fue pensado —la
     * línea que entra y está a unos pixeles—, que es la afordancia que este efecto
     * defiende y que no se pierde.
     */
    const falta = nodo.scrollHeight - nodo.clientHeight - nodo.scrollTop
    const esUnaReubicacion = falta > nodo.clientHeight
    estabaAlFondo.current = true
    nodo.scrollTo({
      top: nodo.scrollHeight,
      behavior: quiereMenos || esUnaReubicacion ? "auto" : "smooth",
    })
  }, [hilo.length, antecedentes.length, ultimoAntecedente])

  /**
   * ⭐⭐ `AW40` · **AL REDIMENSIONAR, EL HILO NO SE QUEDA VARADO.**
   *
   * ⛔⛔ Defecto MEDIDO en Chrome real el 31/8/2026, y **preexistente**: ni `AW37` ni
   * `AW38` lo introdujeron —el efecto del lienzo nunca escuchó al redimensionado—.
   * Achicando la ventana de 1440×900 a 1280×650 con el hilo al fondo:
   *
   * ```
   * scrollTop 41.198   ·   max 56.068   ⇒ VARADO a 14.870 px del final
   * ```
   *
   * Al cambiar el ancho las burbujas envuelven distinto y `scrollHeight` crece, pero
   * **ninguna dep del efecto de arriba cambia** (`hilo.length`, `antecedentes.length`
   * y `ultimoAntecedente` siguen iguales) ⇒ nadie vuelve a rodar. Es la misma familia
   * que el ① del dueño: **el último mensaje queda fuera de la vista.**
   *
   * ⛔ **Sólo actúa si el operador ESTABA mirando el final.** Si había subido a leer
   * algo viejo, un redimensionado que lo tire al fondo le saca lo que estaba leyendo
   * —el mismo daño, en espejo—.
   *
   * ⛔ Y va SIEMPRE instantáneo: un redimensionado no es la llegada de una línea, es
   * el mismo contenido midiendo distinto. Animarlo sería inventar un movimiento que
   * nadie pidió.
   */
  useEffect(() => {
    const nodo = lienzo.current
    if (nodo === null) return undefined
    // ⚠️ El DOM de la suite no lo trae. Sin esto, montar ahí lanza.
    if (typeof ResizeObserver === "undefined") return undefined
    const observador = new ResizeObserver(() => {
      if (!estabaAlFondo.current) return
      if (altoAntesDePegar.current !== null) return
      nodo.scrollTo({ top: nodo.scrollHeight, behavior: "auto" })
    })
    observador.observe(nodo)
    return () => {
      observador.disconnect()
    }
  }, [])

  /**
   * Manda un entrante y **anota lo que el motor contestó**.
   *
   * ⭐ **La burbuja no aparece hasta que el motor contestó.** Dibujarla al apretar
   * sería una confirmación de la pantalla: diría «esto entró» antes de que nada lo
   * hubiera dicho. Con `CH8` diferido, la mitad de los gestos **no producen
   * conducta**, así que una burbuja optimista es el fake que `ADR-0040 §②` prohíbe.
   */
  async function mandar(
    resumen: string,
    tipo: string,
    contenido: string | null,
    interactivo: { readonly buttonId: string; readonly text: string } | null,
  ): Promise<void> {
    setEnviando(true)
    /*
     * ⭐⭐ `AW59` · **LA MARCA DE AGUA SE TOMA ACÁ, ANTES DEL PEDIDO.** Es el
     * instante más nuevo de las salientes que la pantalla **ya tenía**, de las dos
     * fuentes. Tomarla después del `await` la contaminaría con lo que el sondeo o la
     * relectura del historial hayan traído mientras el `POST` viajaba — incluido el
     * redibujado de la respuesta anterior, que es justo lo que esta marca existe
     * para no confundir con una respuesta nueva.
     */
    const marcaDeAgua = instanteMasNuevoDeLasSalientes(hilo, antecedentes)
    try {
      const desenlace = await borde.enviar({
        canal: canalListo,
        de: participanteListo,
        providerMessageId: nuevoIdDeMensaje(),
        tipo,
        texto: contenido,
        interactivo,
      })
      /*
       * ⭐⭐ `AW59` · **SÓLO `encolado` ENCIENDE EL INDICADOR** —es el único de los
       * ocho que ejecuta el turno— y **lo dijo el cable, no la pantalla**. Con
       * cualquier otro desenlace **no hay ningún trabajo en curso**, así que un
       * turno anotado que hubiera quedado de un envío anterior se borra en vez de
       * sobrevivir a un gesto que no encoló nada.
       */
      setTurno(desenlace === DESENLACE_ENCOLADO ? { desde: Date.now(), marcaDeAgua } : null)
      setHilo((anterior) => [
        ...anterior,
        { clase: "entrante", id: proximoId.current++, resumen, tipo, desenlace, fallo: null },
      ])
    } catch (causa) {
      /*
       * ⭐ `AW59` · **El pedido NO LLEGÓ ⇒ no hay turno, y el anotado anterior se
       * va.** Un fallo de red no es un desenlace: nadie dijo que algo se encolara, y
       * dejar el indicador prendido acá sería la obligación fingida en su forma más
       * literal — la pantalla prometiendo una respuesta a un mensaje que ni siquiera
       * salió.
       */
      setTurno(null)
      setHilo((anterior) => [
        ...anterior,
        {
          clase: "entrante",
          id: proximoId.current++,
          resumen,
          tipo,
          desenlace: null,
          fallo: causa instanceof Error ? causa.message : String(causa),
        },
      ])
    } finally {
      setEnviando(false)
      /*
       * ⭐ `AW29` · Relee la sesión, porque **un entrante del operador MUEVE el
       * anclaje** y el `POST /entrada` no devuelve ni el `id_sesion` ni el instante
       * que el motor le puso. Se hace también cuando el pedido falló: si falló del
       * lado de la red, la relectura sale vacía o igual y no miente; si el motor
       * contestó y la pantalla no lo entendió, la relectura lo muestra.
       */
      setPulsoDeSesion((n) => n + 1)
    }
  }

  /**
   * ⭐⭐ `AW30` · **Cierra la sesión vigente del hilo largo, y RELEE.**
   *
   * ⛔ **La relectura no es cosmética.** Cerrar cambia el estado del otro lado y el
   * hilo local no se entera: sin esto, la pantalla seguiría mostrando la sesión
   * cerrada con su contador corriendo, que es exactamente el defecto que `AW28`
   * vino a cerrar, ahora provocado por la propia pantalla. Es el mismo `pulso` que
   * usa `mandar`, y por el mismo motivo.
   *
   * ⚠️ **Se relee también cuando el pedido falló**, igual que después de mandar: si
   * falló del lado de la red no cambió nada y la relectura no miente; si el borde
   * cerró y la respuesta se perdió, la relectura lo muestra. La alternativa
   * —releer sólo cuando anduvo— deja la pantalla afirmando un estado viejo justo en
   * el caso en que nadie sabe cuál es el estado.
   *
   * ⛔ **El `id_sesion` vuelve tal cual, `null` incluido.** Acá no se decide qué
   * significa: el borde de `admin-web` contesta lo mismo para «no había ninguna
   * sesión abierta» y para «ese hilo no existe», y quien lo dibuja
   * (`cierre-de-sesion.tsx` §②) tampoco elige.
   */
  async function cerrarSesionVigente(id_conversacion: string): Promise<string | null> {
    try {
      return await bordeConversaciones.cerrarSesionVigente(id_conversacion)
    } finally {
      setPulsoDeSesion((n) => n + 1)
    }
  }

  /**
   * ⭐⭐ `AW41` · **UN MENSAJE, UNA BURBUJA — el estado deja de ser una línea aparte.**
   *
   * ⛔⛔ Pedido del dueño, textual: *«en los mensajes se está duplicando el que envío,
   * aparece mi mensaje y luego aparece otro diciendo encolado… el estado es importante
   * pero no es un mensaje adicional»*. **Reproducido en Chrome real**: mandé
   * `sonda-dup-01` y salió DOS veces —una en `ANTECEDENTES` con su instante real, otra
   * en el HILO VIVO con el desenlace—.
   *
   * ⇒ **la fila del historial gana, y se queda con el desenlace de la local.** Gana
   * porque es la que el MOTOR escribió: trae el `fecha_hora` real, y el `POST /entrada`
   * devuelve `{ outcome }` y nada más (ver el hueco declarado en `hilo.tsx`), así que
   * la burbuja local **nunca tuvo instante**. Quedarnos con la local sería preferir la
   * versión sin fecha de un hecho que el motor ya fechó.
   *
   * ⛔⛔ **SE EMPAREJA POR CONTENIDO, Y ES UNA LLAVE POBRE. Dicho, no tapado.**
   * `POST /entrada` no devuelve `id_sesion` ni instante ni identificador de mensaje, y
   * `FilaDeMensaje` no trae el desenlace ⇒ **no hay llave natural entre los dos lados**.
   * Lo único comparable es el texto.
   * ⚠️ Consecuencia, y no se disimula: si el operador manda **el mismo texto dos veces
   * en la misma pestaña**, los dos desenlaces se reparten entre las dos filas del
   * historial **en el orden en que se mandaron**, que es lo correcto mientras los
   * desenlaces coincidan y es adivinado si difieren. Se consume de a uno (`usadas`)
   * justamente para no pegarle el mismo desenlace a dos filas.
   * ⇒ **cerrarlo de verdad exige que `POST /entrada` devuelva el identificador de la
   * fila que escribió. Eso es contrato, y no es mío.**
   */
  const usadas = new Set<number>()
  const antecedentesConEstado: readonly LineaDeSesion[] = antecedentes.map((linea) => {
    /*
     * ⭐⭐⭐ `AW55` · **LA OTRA MITAD DE LA DUPLICACIÓN — la del BOT.**
     *
     * ⛔⛔ Acá decía `if (linea.es_sistema) return linea`, y ese `return` temprano
     * **era el defecto**: una fila del sistema salía sin marcar nada en `usadas`, y
     * el filtro de abajo —`hilo.filter((_, idx) => !usadas.has(idx))`— no podía
     * sacar una saliente jamás. `AW41` no estaba roto: estaba **acotado a los
     * entrantes**. Por eso el mensaje del operador salía una vez y la respuesta del
     * bot salía dos: **son dos caminos distintos y sólo uno tenía deduplicador.**
     *
     * ⛔⛔ Pedido del dueño, textual (`H-AUD-018`): *«Los mensajes repetidos —el
     * historial— se muestran apenas envío un mensaje; luego aparece el mensaje
     * nuevo»*, y *«la burbuja repetida conserva la marca de tiempo original»* — o
     * sea **la misma entrega dibujada otra vez, no una entrega nueva**. La base
     * guarda una sola fila, con `seq` correlativo: es un defecto de dibujado.
     *
     * **El mecanismo, entero.** La pantalla lee el mismo hecho de DOS fuentes: el
     * sondeo acumula en `hilo` lo que trae `GET /entregas` de `bot-web` con
     * `clase: "saliente"`, y `setPulsoDeSesion` relee ENTERA `GET /conversaciones`
     * de `admin-web` después de **cada** envío —y esa lectura trae las dos voces,
     * la del bot incluida—. `hilo.tsx` dibuja las dos listas en el MISMO `<ol>`.
     * ⇒ la respuesta del bot está en las dos y se dibuja dos veces; y como la
     * relectura es entera y el `hilo` sólo crece, **el bloque duplicado gana un
     * renglón por turno**, que es exactamente lo que el dueño describió.
     *
     * ⛔ **Y la decisión vive ACÁ, no en `hilo.tsx`.** Filtrar al dibujar sería más
     * corto y rompería dos cosas: `hilo.tsx` arma `sesiones` con
     * `[...antecedentes, ...salientes]` **antes** de dibujar y `estadoDeLaVentana`
     * come esa lista, así que deduplicar después dejaría el conteo de sesiones
     * alimentado con las filas dobles; y partir la misma decisión en dos archivos
     * haría que el día que alguien toque uno, el otro siga afirmando lo contrario
     * en un comentario.
     *
     * ⚠️⚠️ **ES UNA HEURÍSTICA, Y ACÁ VA LO QUE PIERDE — sin redacción amable.**
     * La llave es DERIVADA —`contenido` + instante + `id_sesion`— porque no hay
     * ninguna natural: `POST /entrada` devuelve `{ outcome }` y nada más
     * (`apps/bot-web/src/main.ts`), y ninguno de los dos cables lleva un
     * identificador de mensaje. ⇒ **dos respuestas del bot IDÉNTICAS, en la misma
     * sesión y en el MISMO instante, se colapsan en UNA y se pierde una burbuja
     * legítima.** Eso puede ocurrir de verdad —un flujo que manda la misma
     * plantilla dos veces— y es el defecto **opuesto** al que esto cierra. Se
     * escribe, no se disimula: es exactamente la falta que el hueco declarado de
     * `hilo.tsx` cometió —enumerar las consecuencias que se le ocurrieron a uno y
     * llamarlo «hueco declarado»— y que esta etapa existe para reparar.
     * ⇒ **cerrarlo de verdad exige que `POST /entrada` devuelva el identificador de
     * la fila que escribió, igual que para los entrantes. Eso es contrato, y no es
     * mío.**
     *
     * ⛔⛔ **SE COMPARA POR INSTANTE, NO POR IGUALDAD DE LAS CADENAS ISO.** Hoy los
     * dos textos coinciden porque los dos salen de un `.toISOString()` de la misma
     * columna —`apps/bot-web/src/entregas.ts` de un lado, el `.toISOString()` de
     * arriba sobre el `Date` que revivió el cable, del otro—. ⚠️ **Pero nada lo
     * hace cumplir**: la igualdad de las dos cadenas depende de una ida y vuelta por
     * `Date` en un archivo y de un `.toISOString()` en otro paquete del monorepo,
     * sin un solo examen que los ate. Con `===` sobre el texto, el día que un
     * serializador cambie el sufijo o los milisegundos **el deduplicador dejaría de
     * deduplicar y ningún examen se enteraría**: el defecto volvería igual, con el
     * arreglo puesto. `Date.parse` cuesta lo mismo y no depende de ese invariante.
     *
     * ⚠️ Un instante ilegible da `NaN`, y `NaN === NaN` es `false` ⇒ **no empareja
     * y las dos burbujas quedan**. Es el lado seguro a propósito: de los dos modos
     * de falla, dibujar de más es el que el operador puede ver y diagnosticar.
     *
     * ⭐ **Se consume de a uno** (`usadas`, el mismo conjunto que los entrantes: un
     * índice de `hilo` es de una clase o de la otra, nunca de las dos) para que dos
     * antecedentes gemelos no marquen la misma saliente.
     */
    if (linea.es_sistema) {
      const instante = Date.parse(linea.fecha_hora)
      const j = hilo.findIndex(
        (l, idx) =>
          l.clase === "saliente" &&
          !usadas.has(idx) &&
          l.contenido === linea.contenido &&
          l.id_sesion === linea.id_sesion &&
          Date.parse(l.fecha_hora) === instante,
      )
      if (j !== -1) usadas.add(j)
      /*
       * ⛔ La fila del historial se devuelve TAL CUAL. Una saliente no tiene
       * desenlace que copiarle —`Entrega` no lo lleva— y lo único que esta rama
       * decide es que la gemela viva no se vuelva a dibujar.
       */
      return linea
    }
    const i = hilo.findIndex(
      (l, idx) => l.clase === "entrante" && !usadas.has(idx) && l.resumen === linea.contenido,
    )
    if (i === -1) return linea
    const local = hilo[i]
    if (local === undefined || local.clase !== "entrante") return linea
    usadas.add(i)
    /*
     * ⭐⭐ `AW44` · **EL ACUSE SE CONSUME; LA FALLA SE QUEDA.**
     *
     * ⛔⛔ Pedido del dueño, textual: *«dice encolado, pero el mensaje ya fue procesado
     * y el estado encolado no se actualiza… no es consistente con un mensaje que se
     * cargó de la memoria… primero que fueran consistentes y segundo que el estado se
     * actualice»*.
     *
     * **`encolado` no «no se actualiza»: es un acuse de un instante dibujado como si
     * fuera un atributo permanente del mensaje.** Cuando la fila ya volvió del
     * historial, el turno **ya corrió** —el bot contestó— y decir «quedó encolado» es
     * afirmar un presente que pasó. ⇒ **se consume, y la burbuja queda idéntica a
     * cualquier otra del historial.** Las dos consistencias que pidió, con una regla.
     *
     * ⛔⛔ **PERO SÓLO `encolado`, Y ESTO NO ES UN DETALLE.** `AW27` armó las tres
     * familias para que un rechazo **no pase inadvertido**: `rechazado` y `sin-motor`
     * son FALLAS y **se quedan dibujadas**. Desvanecerlas «por consistencia» sería
     * esconder los errores, que es el defecto opuesto y peor.
     *
     * ⚠️ Y un envío que NUNCA llega al historial —`emisor-desconocido` no persiste
     * fila— no entra por acá: **conserva su burbuja local con su desenlace**, que es
     * el único lugar donde esa falla se ve.
     *
     * ⛔ **El `tipo` tampoco se copia.** `FilaDeMensaje` no lo trae (ver el contrato),
     * así que mostrarlo sólo en las que mandó ESTA pestaña **es exactamente la
     * asimetría que el dueño señaló**: dos burbujas del mismo participante con
     * anatomías distintas según quién las cargó.
     */
    const esAcuse = local.desenlace === DESENLACE_ENCOLADO && local.fallo === null
    if (esAcuse) return linea
    return { ...linea, desenlace: local.desenlace, fallo: local.fallo }
  })
  /** Las locales que YA tienen su gemela en el historial no se vuelven a dibujar. */
  const hiloSinDuplicados = hilo.filter((_, idx) => !usadas.has(idx))

  /**
   * ⭐⭐ `ADR-0292` / `B22` · **CADA SALIENTE SE LLEVA SU FILA DEL REGISTRO.**
   *
   * ⛔ **La propiedad se agrega sólo cuando hay turno, y no se escribe `undefined`.**
   * `exactOptionalPropertyTypes` está encendido (`tsconfig.json`) y distingue las dos
   * cosas —que es exactamente la distinción que `hilo.tsx` dibuja: ausente es
   * «todavía no llegó» y no «no hay»—.
   *
   * ⛔ Y los entrantes pasan tal cual: `turn_log` registra **el turno del motor**, o
   * sea lo que produjo una RESPUESTA. Colgarle metadatos de turno a la burbuja de lo
   * que el operador mandó sería afirmar que ese turno es suyo, que es justo lo que la
   * correlación por tiempo no puede afirmar.
   */
  const hiloConRegistro: readonly EnElHilo[] = hiloSinDuplicados.map((linea) => {
    if (linea.clase !== "saliente") return linea
    const registro = registros.get(linea.id)
    return registro === undefined ? linea : { ...linea, registro }
  })

  /**
   * ⭐⭐ `AW59` · **EL ESTADO DEL INDICADOR, DERIVADO EN EL RENDER.**
   *
   * ⛔ **Cero `setTimeout` y cero `setInterval` nuevos**: la pantalla ya se
   * re-renderiza en cada tic del sondeo, que es la mejor resolución que este
   * apagado puede tener —la respuesta sólo se conoce cuando `GET /entregas` la
   * trae—. Agendar un temporizador propio no adelantaría ninguna información: sólo
   * adelantaría el borrado.
   *
   * ⚠️ **Y la granularidad queda escrita en vez de descubierta:** el techo se alcanza
   * con la resolución de `config.refrescoMs`, así que una espera máxima declarada se
   * puede notar hasta un intervalo más tarde. ⛔ Eso NO se arregla bajando el
   * refresco —que es el valor de una tabla y no el de un chat (§⑥)— ni agendando un
   * temporizador. Hueco heredado, dueño `web-dev` con el TL.
   */
  const indicadorDelTurno = estadoDelTurno({
    turno,
    esperaMaximaMs: config.esperaMaximaDelSimuladorMs,
    instanteMasNuevo: instanteMasNuevoDeLasSalientes(hilo, antecedentes),
    ahora,
  })

  /**
   * ⛔ `AW59` · **El indicador NO entra acá, y es la regla y no el maquetado.**
   * `puedeMandar` no lo mira: mandar otro mensaje con un turno en curso es legítimo.
   */
  const puedeMandar = conversacionArmada && !enviando

  return (
    <section className="simulador" aria-labelledby="simulador-titulo">
      <header className="simulador__encabezado">
        <h1 id="simulador-titulo">Simulador de chat</h1>
      </header>

      {/*
        ⭐⭐ RASTRO · `AW32` · 28/8/2026 — **ESTA NOTA ESTABA DENTRO DEL `<header>`
        Y POR ESO QUEDABA ANTES DEL FILETE.** No era un descuido de márgenes: el
        filete lo dibuja el envoltorio `.simulador__encabezado`, así que todo lo que
        vive adentro queda arriba de la línea y todo lo que vive afuera queda abajo.
        Carga en lote y Respuestas automáticas ya tenían la suya afuera y quedaban
        DESPUÉS; ésta era la única de las tres del otro lado.

        ⇒ **estandarizar a DESPUÉS es sacarla del `<header>`, no agregarle un
        margen.** Un margen la habría bajado dejándola igual de adentro, y la
        siguiente pantalla que copie este encabezado se habría llevado el defecto.
      */}
      <p className="nota" role="note">
        Motor real, canal simulado, base local. Un pase medido contra esta pantalla no es un pase
        contra el sistema real: ningún caso de QA ejercido acá se publica como{" "}
        <code>sujeto: real</code>.
      </p>

      {/*
        ⭐ LA FORMA ES LA DE UN CLIENTE DE MENSAJERÍA, EL MUNDO ES EL DE LA HERRAMIENTA.
        Dos paneles: a la izquierda con quién se habla, a la derecha la conversación.
        ⛔ Y sin esquinas redondeadas ni burbujas de color: `DESIGN.md` §Radios lo fija
        en cero —«un formulario impreso no tiene esquinas redondeadas»— y la estrategia
        de color es restringida, así que el acento es del brazo y de la selección, no
        del chat. Lo que separa una línea de otra es **de qué lado está y sobre qué
        superficie**, no un color de mensajería.
      */}
      <div className="sim">
        <aside className="sim__lateral" aria-label="Con quién se habla">
          <div className="sim__campo">
            <label htmlFor="simulador-canal">Canal receptor</label>
            {/*
              ⭐ `AW23` · Lista cerrada cuando la instancia declaró sus canales.
              ⛔ Y `<input>` de texto libre cuando NO los declaró: no se dibuja un
              `<select>` vacío y no se inventa un canal.
            */}
            {canales.length > 0 ? (
              <select
                id="simulador-canal"
                value={canal}
                onChange={(evento) => setCanal(evento.target.value)}
              >
                {canales.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                {/*
                  ⛔ El escape NO se saca: es el único camino para producir
                  `canal-no-ruteable` a pedido, que es uno de los ocho desenlaces.
                  Un selector que TAPA un desenlace reduce el instrumento mientras
                  lo hace cómodo — la misma regla que la dirección a mano.
                */}
                <option value={CANAL_A_MANO}>Otro (a mano)…</option>
              </select>
            ) : (
              <input
                id="simulador-canal"
                type="text"
                value={canal}
                onChange={(evento) => setCanal(evento.target.value)}
              />
            )}
            {canal === CANAL_A_MANO && (
              <input
                aria-label="Canal receptor a mano"
                type="text"
                value={canalAMano}
                onChange={(evento) => setCanalAMano(evento.target.value)}
              />
            )}
            <p className="sim__ayuda">
              El brazo sale de acá y no se elige. Si no se puede rutear, el motor contesta{" "}
              <code>canal-no-ruteable</code>.
            </p>
          </div>

          <SelectorParticipante borde={bordePadron} elegido={elegido} onElegir={setElegido} />

          <details className="sim__escape">
            <summary>Dirección a mano</summary>
            <div className="sim__campo">
              <label htmlFor="simulador-participante">Dirección a mano (escape)</label>
              {/* ⛔ Sin `placeholder` y sin ejemplo: el formato se DESCRIBE en palabras. */}
              <input
                id="simulador-participante"
                type="text"
                value={aMano}
                disabled={elegido !== null}
                onChange={(evento) => setAMano(evento.target.value)}
              />
              <p className="sim__ayuda">
                El único camino para producir <code>emisor-desconocido</code> a pedido. Se ignora
                mientras haya un participante elegido del padrón.
              </p>
            </div>
          </details>
        </aside>

        <div className="sim__conversacion">
          <header className="sim__cabecera">
            {conversacionArmada ? (
              <>
                <span className="sim__con-quien">
                  {elegido !== null ? elegido.nombre : "Dirección a mano"}
                </span>
                <code className="sim__direccion">{participanteListo}</code>
                <span className="sim__canal">por {canalListo}</span>
              </>
            ) : (
              <span className="sim__con-quien sim__con-quien--sin">Sin conversación</span>
            )}
          </header>

          {/*
            ⭐⭐ `AW39` · **EL DISPARADOR DE LA PAGINACIÓN INFINITA.** Se escucha el
            rodado del lienzo y se pide la página siguiente al acercarse al tope.

            ⛔ **80 px y no 0**: pedir exactamente en 0 obliga al operador a chocar
            contra el borde y esperar; con un margen, lo viejo ya está cuando llega.
            ⛔ Y `traerMasViejas` se protege sola de la reentrada (`cargandoMas`), que
            es necesario porque un rodado dispara MUCHOS eventos seguidos.
          */}
          <div
            className="sim__lienzo"
            ref={lienzo}
            onScroll={(evento) => {
              const nodo = evento.currentTarget
              // ⭐ `AW40` · 40 px de tolerancia: el rodado deja restos fraccionarios y
              //    exigir la igualdad exacta haría que «estaba al fondo» casi nunca sea
              //    cierto.
              estabaAlFondo.current = nodo.scrollHeight - nodo.clientHeight - nodo.scrollTop < 40
              if (nodo.scrollTop > 80) return
              void traerMasViejas()
            }}
          >
            {/*
              ⭐ `AW39` · El estado de la paginación, ARRIBA de todo, que es de donde
              viene lo que se está por cargar.
            */}
            {conversacionArmada && antecedentes.length > 0 && (
              <p className="sim__ayuda" role="status">
                {cargandoMas
                  ? "Trayendo mensajes anteriores…"
                  : antecedentes.length < totalEnBase
                    ? `Mostrando los ${String(antecedentes.length)} más recientes de ${String(totalEnBase)}. Rodá hacia arriba para traer los anteriores.`
                    : `Estos son los ${String(totalEnBase)} mensajes de la conversación: no hay nada anterior.`}
              </p>
            )}
            {errorDeLectura !== null && (
              <p className="simulador__error" role="alert">
                No se pudieron leer las respuestas: {errorDeLectura}
              </p>
            )}

            {!conversacionArmada && (
              <div className="simulador__vacio">
                <p>
                  Elegí un canal receptor y un participante. Sin los dos no se manda nada y no se
                  lee nada: la lectura va siempre acotada a un participante.
                </p>
              </div>
            )}

            {/*
              ⚠️ `AW28` · Cuando `admin-web` no contesta, **se dice** — y se nombra la
              causa más probable, que es la que el operador no puede adivinar: este
              cable va con la cookie de Better Auth y el resto de la pantalla no.
              ⛔ Dibujar un hilo vacío acá se leería como «no hay conversación».
            */}
            {conversacionArmada && errorDeSesion !== null && (
              <p className="simulador__error" role="alert">
                No se pudo leer la conversación anterior: {errorDeSesion} Esta lectura va contra{" "}
                <code>admin-web</code> con tu sesión de operador, a diferencia del resto del
                simulador: si no estás autenticado, lo que falta acá es la sesión y no la
                conversación.
              </p>
            )}

            {conversacionArmada && leyendoSesion && (
              <p className="sim__ayuda" role="status">
                Leyendo la conversación anterior…
              </p>
            )}

            {conversacionArmada &&
              hilo.length === 0 &&
              antecedentes.length === 0 &&
              !leyendoSesion &&
              errorDeSesion === null && (
                <div className="simulador__vacio">
                  <p>
                    No hay ninguna sesión escrita para este participante, y todavía no se mandó
                    nada por esta conversación.
                  </p>
                </div>
              )}

            {conversacionArmada && (hilo.length > 0 || antecedentes.length > 0) && (
              <Hilo
                lineas={hiloConRegistro}
                antecedentes={antecedentesConEstado}
                ahora={ahora}
                onCerrarSesion={cerrarSesionVigente}
                errorDeRegistro={errorDeTurnos}
              />
            )}

            {/*
              ⭐⭐ `AW59` · **EL INDICADOR DE TURNO EN CURSO, Y SU AVISO AL PASARSE
              DE LA ESPERA MÁXIMA.**

              ⛔⛔ **VA ACÁ AFUERA, Y NO ES UNA DECISIÓN DE MAQUETADO: ES LA FORMA EN
              QUE ESTO CUMPLE `ADR-0040 §②`.** El §③ de este archivo es textual:
              *«todo lo que se ve en el hilo llegó por el cable»* — y este elemento
              **no llegó por el cable, lo derivó la pantalla**. ⇒ vive **fuera del
              `<ol className="simulador__hilo">`**, **no lleva `.simulador__linea`**,
              **no es una `.sim__burbuja`** y **no entra en `lineas` ni en
              `antecedentes`**. Es un estado de la PANTALLA dicho como estado de la
              pantalla, y así el operador no lo puede confundir con un hecho del
              sistema.

              ⚠️ **La opción prohibida es la más linda**: agregarlo como una línea
              más con una clase propia —`.simulador__linea--pensando`— hereda el
              layout del hilo, queda alineado con las burbujas del bot y se ve
              exactamente como el «Typing…» de cualquier chat. Falla por lo de
              arriba, y falla de un segundo modo más concreto: `hilo.tsx` arma
              `sesiones` con `[...antecedentes, ...salientes]` **antes** de dibujar y
              el estado de la ventana come esa lista, así que una línea sintética
              contaminaría el conteo de sesiones con algo que no es un mensaje.

              ⚠️ **Y hay una trampa de medición que lo hace parecer inocuo:** el caso
              de `aw16` que cuenta líneas mientras el motor no contestó cuenta
              `.simulador__linea`, así que **una clase distinta lo dejaría verde
              igual**. O sea que el examen que parece custodiar esto **no custodia lo
              que importa**, y la disciplina viene de acá.

              ⛔ `role="status"` y no `role="alert"`: esto no interrumpe a nadie, es
              el estado de una región que se actualiza sola.
            */}
            {conversacionArmada && indicadorDelTurno !== null && (
              <p
                className={`sim__turno sim__turno--${indicadorDelTurno}`}
                data-turno={indicadorDelTurno}
                role="status"
              >
                {/*
                  ⭐ RASTRO · 16/9/2026 · **EL COPY DE EN-CURSO CAMBIÓ A PEDIDO DEL DUEÑO.**

                  Acá decía, textual: *«Turno en curso: el motor contestó `encolado` y la
                  pantalla sigue preguntando.»* — chico, gris y hablando de `encolado`. El
                  dueño había pedido *«un feedback que diga algo como "Typing..."»* y eso no
                  se le parecía; lo eligió el implementador y lo declaró como elección suya.

                  ⛔ **POR QUÉ ESTE CAMBIO NO VIOLA `ADR-0040 §②`, que es lo único que hay
                  que verificar antes de tocar este texto.** La regla es *«se puede fingir lo
                  que no promete nada que el sistema no vaya a cumplir. NO SE PUEDE FINGIR UNA
                  OBLIGACIÓN.»* Las siete formulaciones que la etapa prohíbe por su nombre
                  —«el bot no contestó», «timeout», «se perdió»…— son todas del caso de
                  FALLA, y están prohibidas porque ahí la pantalla **no puede distinguir un
                  turno muerto de una respuesta que todavía no vio**.

                  Este texto es del otro caso: sólo se dibuja mientras `desenlace` volvió
                  `encolado` y ninguna saliente cruzó la marca de agua, o sea **mientras el
                  turno corre de verdad**. Ahí el indicador no promete nada que el sistema no
                  vaya a hacer ⇒ cae del lado PERMITIDO, que es el argumento entero de la
                  etapa.

                  ⭐⭐ **Y lo que hace que siga siendo publicable NO es este texto: es el
                  VENCIMIENTO**, que no se tocó. Un indicador sin techo es la versión
                  prohibida de esta etapa, diga lo que diga. Si alguien alguna vez saca el
                  `AL_PASARSE_LA_ESPERA` de abajo «porque el cartel ya se entiende», rompe
                  exactamente lo que este bloque protege.
                */}
                {indicadorDelTurno === "en-curso" ? "Escribiendo…" : AL_PASARSE_LA_ESPERA}
              </p>
            )}
          </div>

          <Redactor
            config={config}
            puedeMandar={puedeMandar}
            onMandar={(resumen, tipo, contenido, interactivo) => {
              void mandar(resumen, tipo, contenido, interactivo)
            }}
          />

          {/*
            ⭐⭐ `ADR-0292` / `B22` · **EL ÚLTIMO TURNO DEL BRAZO.**

            QUÉ: `seq`, milisegundos y camino de la última fila de
            `conversation.turn_log` del brazo del canal elegido.
            POR QUÉ: es lo que un QA mira cuando pregunta *«cuánto tarda en
            responder»*, y —esto es lo que lo hace valer el espacio— **sirve aunque la
            correlación por tiempo falle**: no depende de emparejar nada con una
            entrega, sale derecho del registro.

            ⛔⛔ **Y dice «del brazo», no «de tu mensaje».** Es la última de TODOS los
            participantes del brazo: si alguien más está conversando, este número es
            suyo. Rotularlo «tu última respuesta» sería la misma clase de afirmación
            sin respaldo que esta pantalla no hace en ningún otro lado.

            ⛔ Va al PIE y no arriba: arriba está con quién se habla, que es lo que se
            lee primero. Esto es instrumental, y se busca cuando se lo necesita.
          */}
          {conversacionArmada && (
            <p className="sim__ultimo-turno" role="status" aria-label="último turno del brazo">
              <span className="sim__rotulo">Último turno del brazo</span>
              {errorDeTurnos !== null ? (
                <span>registro no disponible ({errorDeTurnos})</span>
              ) : ultimoTurno === null ? (
                <span>registro pendiente…</span>
              ) : (
                <span>
                  <code>#{String(ultimoTurno.seq)}</code>
                  {" · "}
                  {FORMATO_MS.format(ultimoTurno.ms_transcurridos)} ms{" · "}
                  <code>{ultimoTurno.camino}</code>
                </span>
              )}
            </p>
          )}

          <p className="sim__pie">
            Las respuestas se PIDEN, no llegan solas: se vuelve a preguntar cada {config.refrescoMs}{" "}
            ms con el cursor anterior. Sólo las de este participante.
          </p>
        </div>

        {/*
          ⭐ `AW24` · La tercera columna. Ausente el borde ⇒ NO se monta: escribe un
          envío real en el estudio, así que es opt-in de la instancia.
        */}
        {bordeEnvios !== undefined && (
          <ColumnaDePlantilla
            borde={bordeEnvios}
            elegido={elegido}
            cargarPlantillas={cargarPlantillas ?? SIN_CATALOGO_DE_PLANTILLAS}
          />
        )}
      </div>
    </section>
  )
}
